import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Switch } from './components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from './components/ui/dialog';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Users, FileText, TrendingUp, TrendingDown, Settings, Plus, Edit, Trash2, Eye, BarChart3, LogOut, User, Shield, MessageCircle, Send, MessageSquare, Clock, Filter, CheckSquare, Receipt, Upload, CreditCard, AlertCircle, CheckCircle, XCircle, Mail, Share, Menu, X, Link as LinkIcon, Download, RefreshCw, QrCode, Layout, FileEdit, Database, ExternalLink, Search, Brain, Bell, Globe, Building2 } from 'lucide-react';
import QRCode from 'qrcode';
import { QRCodeSVG } from 'qrcode.react';
import './App.css';

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Page imports
import TaskTemplates from './pages/TaskTemplates';
import Tasks from './pages/Tasks';
import StaffManagement from './pages/StaffManagement';
import UserSettings from './pages/UserSettings';
import WhatsAppManager from './pages/WhatsAppManager';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailVerification from './pages/EmailVerification';
import Services from './pages/Services';
import Invoices from './pages/Invoices';
import Receipts from './pages/Receipts';
import Expenses from './pages/Expenses';
import Documents from './pages/Documents';
import Communications from './pages/Communications';
import BulkUpload from './pages/BulkUpload';
import PaymentStatus from './pages/PaymentStatus';
import GstReturnTracker from './pages/GstReturnTracker';
import APIKeyManagement from './pages/APIKeyManagement';
import UserManagement from './pages/UserManagement';
import ProjectFeatures from './pages/ProjectFeatures';
import ReminderAutomation from './pages/ReminderAutomation';
import AdvancedDashboard from './pages/AdvancedDashboard';
import AuditLogs from './pages/AuditLogs';
import PlatformAnalytics from './pages/PlatformAnalytics';
import ClientPortal from './pages/ClientPortal';
import ClientPortalManagement from './pages/ClientPortalManagement';
import ClientServices from './pages/ClientServices';
import OnboardingWizard from './components/OnboardingWizard';

// Indian Rupee Icon Component
const IndianRupee = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.66 7H16v2h-2.34c-.59 0-1.15.26-1.54.72-.39.46-.59 1.08-.59 1.72 0 .64.2 1.26.59 1.72.39.46.95.72 1.54.72H16v2h-2.34c-1.18 0-2.3-.52-3.08-1.44C9.8 13.64 9.5 12.36 9.5 11s.3-2.64 1.08-3.56c.78-.92 1.9-1.44 3.08-1.44zM5 7h2v2H5V7zm0 4h2v2H5v-2zm7-8h5v2h-5V3zm0 16h5v2h-5v-2zM7 15l7.5 4.5-1 1.5L6 16.5 7 15z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Indian States and Union Territories
const INDIAN_STATES = [
  // 28 States
  { code: 'AP', name: 'Andhra Pradesh' },
  { code: 'AR', name: 'Arunachal Pradesh' },
  { code: 'AS', name: 'Assam' },
  { code: 'BR', name: 'Bihar' },
  { code: 'CG', name: 'Chhattisgarh' },
  { code: 'GA', name: 'Goa' },
  { code: 'GJ', name: 'Gujarat' },
  { code: 'HR', name: 'Haryana' },
  { code: 'HP', name: 'Himachal Pradesh' },
  { code: 'JH', name: 'Jharkhand' },
  { code: 'KA', name: 'Karnataka' },
  { code: 'KL', name: 'Kerala' },
  { code: 'MP', name: 'Madhya Pradesh' },
  { code: 'MH', name: 'Maharashtra' },
  { code: 'MN', name: 'Manipur' },
  { code: 'ML', name: 'Meghalaya' },
  { code: 'MZ', name: 'Mizoram' },
  { code: 'NL', name: 'Nagaland' },
  { code: 'OR', name: 'Odisha' },
  { code: 'PB', name: 'Punjab' },
  { code: 'RJ', name: 'Rajasthan' },
  { code: 'SK', name: 'Sikkim' },
  { code: 'TN', name: 'Tamil Nadu' },
  { code: 'TS', name: 'Telangana' },
  { code: 'TR', name: 'Tripura' },
  { code: 'UK', name: 'Uttarakhand' },
  { code: 'UP', name: 'Uttar Pradesh' },
  { code: 'WB', name: 'West Bengal' },
  
  // 8 Union Territories
  { code: 'AN', name: 'Andaman and Nicobar Islands' },
  { code: 'CH', name: 'Chandigarh' },
  { code: 'DN', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: 'DL', name: 'Delhi' },
  { code: 'JK', name: 'Jammu and Kashmir' },
  { code: 'LA', name: 'Ladakh' },
  { code: 'LD', name: 'Lakshadweep' },
  { code: 'PY', name: 'Puducherry' }
];

// Auth Context now imported from contexts/AuthContext.jsx

// Protected Route Component
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requireSuperAdmin && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Landing Page Component
const LandingPage = () => {
  const { user } = useAuth();
  const [showSignup, setShowSignup] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  
  // Auto-open login modal when switching accounts
  const landingLocation = useLocation();
  const isSwitchMode = new URLSearchParams(landingLocation.search).get('switch') === 'true';
  const [showLogin, setShowLogin] = useState(isSwitchMode);
  
  // Ensure login modal opens when switch mode is detected (handles re-renders)
  useEffect(() => {
    if (isSwitchMode && !showLogin) {
      setShowLogin(true);
    }
  }, [isSwitchMode]);
  const [content, setContent] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_cta_text: "Get Started Today",
    feature_1_title: "",
    feature_1_description: "",
    feature_2_title: "", 
    feature_2_description: "",
    feature_3_title: "",
    feature_3_description: "",
    feature_4_title: "",
    feature_4_description: ""
  });

  // Fetch landing page content from CMS before rendering
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/cms/content/landing`);
        
        // Set content with fallbacks for missing fields
        setContent({
          hero_title: response.data.hero_title || "Complete GST & Financial Management Platform",
          hero_subtitle: response.data.hero_subtitle || "Complete financial management with automated receipt tracking, expense management, WhatsApp integration, and comprehensive business tools designed for modern tax practitioners.",
          hero_cta_text: response.data.hero_cta_text || "Get Started Today",
          feature_1_title: response.data.feature_1_title || "Receipt Management",
          feature_1_description: response.data.feature_1_description || "Track service fees, tax collections, and advance receipts with comprehensive reporting and invoice linking",
          feature_2_title: response.data.feature_2_title || "Expense Tracking",
          feature_2_description: response.data.feature_2_description || "Manage office expenses, travel costs, tax payments, and professional services with GST calculations",
          feature_3_title: response.data.feature_3_title || "Client & Invoice Management",
          feature_3_description: response.data.feature_3_description || "Comprehensive client profiles with automated GST invoicing, HSNSAC codes, and professional PDF generation",
          feature_4_title: response.data.feature_4_title || "WhatsApp Integration",
          feature_4_description: response.data.feature_4_description || "Multi-tenant WhatsApp Web integration with message templates, bulk messaging, and automated notifications"
        });
        
        setIsLoadingContent(false);
      } catch (error) {
        console.error('Error fetching landing content:', error);
        // Set fallback content if API fails
        setContent({
          hero_title: "Complete GST & Financial Management Platform",
          hero_subtitle: "Complete financial management with automated receipt tracking, expense management, WhatsApp integration, and comprehensive business tools designed for modern tax practitioners.",
          hero_cta_text: "Get Started Today",
          feature_1_title: "Receipt Management",
          feature_1_description: "Track service fees, tax collections, and advance receipts with comprehensive reporting and invoice linking",
          feature_2_title: "Expense Tracking",
          feature_2_description: "Manage office expenses, travel costs, tax payments, and professional services with GST calculations",
          feature_3_title: "Client & Invoice Management",
          feature_3_description: "Comprehensive client profiles with automated GST invoicing, HSNSAC codes, and professional PDF generation",
          feature_4_title: "WhatsApp Integration",
          feature_4_description: "Multi-tenant WhatsApp Web integration with message templates, bulk messaging, and automated notifications"
        });
        setIsLoadingContent(false);
      }
    };

    fetchContent();
  }, []);

  // Redirect authenticated users (but allow access when switching accounts)
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSwitching = searchParams.get('switch') === 'true';
  
  if (user && !isSwitching) {
    if (user.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Show loading state while fetching CMS content
  if (isLoadingContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm shadow-xl border-b border-orange-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <img 
                src="/gstassist-header-logo.png" 
                alt="GST ASSIST Logo"
                className="h-10 sm:h-12 w-auto"
              />
              <div className="flex flex-col justify-center">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold leading-tight" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
                  <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">GST ASSIST</span><sup className="text-[0.5em] sm:text-[0.6em] align-top ml-0.5 font-normal text-orange-600">™</sup>
                </h1>
                <span className="text-[10px] sm:text-xs text-gray-500 font-medium leading-tight">Professional SaaS Platform</span>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-4">
              <Button 
                variant="outline" 
                onClick={() => setShowLogin(true)} 
                data-testid="login-btn"
                className="h-9 sm:h-11 border-2 border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300 font-semibold px-3 sm:px-6 text-sm sm:text-base transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => setShowSignup(true)} 
                data-testid="signup-btn"
                className="h-9 sm:h-11 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-4 sm:px-8 text-sm sm:text-base shadow-md hover:shadow-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center">
          <div className="mb-8">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-orange-100 to-red-100 text-orange-900 mb-6">
              <span className="w-2 h-2 bg-orange-600 rounded-full mr-2 animate-pulse"></span>
              🚀 Trusted by 10,000+ Tax Professionals across India
            </span>
          </div>
          
          <h1 className="text-7xl font-black mb-8">
            <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {content.hero_title}
            </span>
          </h1>
          
          <p className="text-2xl text-gray-600 mb-16 max-w-4xl mx-auto leading-relaxed font-medium">
            {content.hero_subtitle}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
            <Card className="bg-white/90 backdrop-blur-sm border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 p-8 hover:-translate-y-1 rounded-2xl group">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{content.feature_1_title}</h3>
              <p className="text-gray-600 text-base leading-relaxed">{content.feature_1_description}</p>
            </Card>
            
            <Card className="bg-white/90 backdrop-blur-sm border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 p-8 hover:-translate-y-1 rounded-2xl group">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingDown className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{content.feature_2_title}</h3>
              <p className="text-gray-600 text-base leading-relaxed">{content.feature_2_description}</p>
            </Card>
            
            <Card className="bg-white/90 backdrop-blur-sm border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 p-8 hover:-translate-y-1 rounded-2xl group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{content.feature_3_title}</h3>
              <p className="text-gray-600 text-base leading-relaxed">{content.feature_3_description}</p>
            </Card>
          </div>

          {/* Additional Features Section */}
          {content.feature_4_title && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 p-8 hover:scale-105">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <MessageCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{content.feature_4_title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{content.feature_4_description}</p>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 p-8 hover:scale-105">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <FileText className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">Advanced Reporting</h3>
                <p className="text-gray-600 text-lg leading-relaxed">Excel exports with custom date filtering for receipts, expenses, invoices with multiple sheet formats</p>
              </Card>
            </div>
          )}

          {/* PWA Install Section */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl p-8 sm:p-12 mb-12 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">📱 Install Our Mobile App</h2>
                <p className="text-blue-100 text-lg mb-2">Access GST ASSIST anytime, anywhere</p>
                <ul className="text-blue-100 text-left inline-block mt-4 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-green-300">✓</span> Works offline
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-300">✓</span> Faster performance
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-300">✓</span> Home screen access
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-300">✓</span> Push notifications
                  </li>
                </ul>
              </div>
              {/* Install App button removed */}
            </div>
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-12 mb-24 text-white shadow-2xl">
            <h2 className="text-4xl font-bold mb-12">Complete Financial Management Platform</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-black mb-2">10K+</div>
                <div className="text-orange-100 text-lg">Receipts Tracked</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black mb-2">5K+</div>
                <div className="text-orange-100 text-lg">Expenses Managed</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black mb-2">15K+</div>
                <div className="text-orange-100 text-lg">WhatsApp Messages</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black mb-2">99.9%</div>
                <div className="text-orange-100 text-lg">GST Compliance</div>
              </div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="mb-24">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Tax Professionals</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">See what chartered accountants and tax practitioners say about GST ASSIST</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Testimonial 1 */}
              <Card className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 p-8 rounded-2xl">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    RS
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Rajesh Sharma</h4>
                    <p className="text-sm text-gray-600">CA, Mumbai</p>
                    <div className="flex gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"GST ASSIST has transformed how I manage my practice. The automated invoicing saves me 15+ hours every week. Best investment for my CA firm!"</p>
              </Card>

              {/* Testimonial 2 */}
              <Card className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 p-8 rounded-2xl">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    PM
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Priya Mehta</h4>
                    <p className="text-sm text-gray-600">Tax Practitioner, Delhi</p>
                    <div className="flex gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"The WhatsApp integration is a game-changer! I can send invoices and reminders to 100+ clients instantly. Outstanding platform!"</p>
              </Card>

              {/* Testimonial 3 */}
              <Card className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 p-8 rounded-2xl">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    AK
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Anil Kumar</h4>
                    <p className="text-sm text-gray-600">CA, Bangalore</p>
                    <div className="flex gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"Managing 200+ clients was chaos before GST ASSIST. Now everything is organized, compliant, and accessible from anywhere. Highly recommended!"</p>
              </Card>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-24">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about GST ASSIST</p>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Is GST ASSIST suitable for small practices?</h3>
                <p className="text-gray-600 leading-relaxed">Absolutely! Whether you have 10 clients or 1000, GST ASSIST scales with your practice. Start with essentials and add features as you grow.</p>
              </Card>

              <Card className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">How secure is my client data?</h3>
                <p className="text-gray-600 leading-relaxed">We use bank-level 256-bit encryption for all data. Your information is stored on secure cloud servers with daily backups and 99.9% uptime guarantee.</p>
              </Card>

              <Card className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Can I try before I buy?</h3>
                <p className="text-gray-600 leading-relaxed">Yes! Get a free 30-day trial with full access to all features. No credit card required. Cancel anytime if it's not the right fit.</p>
              </Card>

              <Card className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Does it integrate with GST Portal?</h3>
                <p className="text-gray-600 leading-relaxed">Yes, GST ASSIST integrates with the official GST Portal to sync returns, track filing status, and maintain compliance automatically.</p>
              </Card>

              <Card className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">What support options are available?</h3>
                <p className="text-gray-600 leading-relaxed">We offer email support, live chat, comprehensive documentation, and video tutorials. Premium plans include dedicated account managers and phone support.</p>
              </Card>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button 
              size="lg" 
              onClick={() => setShowSignup(true)} 
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-12 py-6 text-xl rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 min-w-[280px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
            >
              Start Your Free Trial
              <span className="ml-2">→</span>
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => setShowLogin(true)}
              className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 font-bold px-12 py-6 text-xl rounded-2xl transition-all duration-300 hover:-translate-y-1 min-w-[280px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-orange-200"
            >
              Sign In
            </Button>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-500 text-lg">
              ✅ Free 30-day trial &nbsp; • &nbsp; ✅ No credit card required &nbsp; • &nbsp; ✅ Cancel anytime
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-200 py-6 mt-20">
          <div className="container mx-auto px-4">
            <p className="text-center text-xs text-gray-500 leading-relaxed">
              SRI LALITHA DIGITAL SOLUTIONS<br />
              18-2-154A/3F, Merukantha Apartment, Ashok Nagar<br />
              Tirupati, Chittoor, Andhra Pradesh - 517501
            </p>
          </div>
        </footer>
      </main>

      {/* Login Modal */}
      <LoginModal open={showLogin} onOpenChange={setShowLogin} />
      
      {/* Signup Modal */}
      <SignupModal open={showSignup} onOpenChange={setShowSignup} />
    </div>
  );
};

// Login Modal Component
const LoginModal = ({ open, onOpenChange }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      onOpenChange(false);
      setFormData({ email: '', password: '' });
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Welcome Back</DialogTitle>
          <DialogDescription className="text-base">
            Sign in to access your GSTAssist dashboard
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4" autoComplete="off">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2" role="alert">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          <div>
            <Label htmlFor="login-email-field" className="text-sm font-semibold">Email Address</Label>
            <Input
              id="login-email-field"
              name="login-email-field"
              type="email"
              data-testid="login-email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              placeholder="john@example.com"
              className="mt-2 h-12 text-base focus-ring"
              autoComplete="off"
              autoFocus
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="login-password-field" className="text-sm font-semibold">Password</Label>
              <a 
                href="/forgot-password" 
                className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  onOpenChange(false);
                  window.location.href = '/forgot-password';
                }}
              >
                Forgot?
              </a>
            </div>
            <Input
              id="login-password-field"
              name="login-password-field"
              type="password"
              data-testid="login-password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
              placeholder="Enter your password"
              className="h-12 text-base focus-ring"
              autoComplete="new-password"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all duration-200" 
            disabled={loading} 
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing In...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Sign In
                <span>→</span>
              </span>
            )}
          </Button>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  // Small delay to avoid modal conflict
                  setTimeout(() => document.querySelector('[data-testid="signup-btn"]')?.click(), 100);
                }}
                className="text-orange-600 hover:text-orange-700 font-semibold transition-colors"
              >
                Sign up free
              </button>
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Signup Modal Component (Simplified - 3 fields only)
const SignupModal = ({ open, onOpenChange }) => {
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    // Default values for backend compatibility
    organization_name: 'My Organization',
    profession: 'CA',
    gstin: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');

  const calculatePasswordStrength = (password) => {
    if (password.length === 0) return '';
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    if (password.length >= 10 && /[A-Z]/.test(password) && /[0-9]/.test(password)) return 'strong';
    return 'medium';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({...formData, password: newPassword});
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await signup(formData);
    
    if (result.success) {
      onOpenChange(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        organization_name: 'My Organization',
        profession: 'CA',
        gstin: ''
      });
      setPasswordStrength('');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const getPasswordStrengthColor = () => {
    switch(passwordStrength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-gray-200';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Start Managing Clients in 60 Seconds</DialogTitle>
          <DialogDescription className="text-base">
            Create your free account • No credit card required
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2" role="alert">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          <div>
            <Label htmlFor="name" className="text-sm font-semibold">Full Name *</Label>
            <Input
              id="name"
              data-testid="signup-name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              placeholder="John Doe"
              className="mt-2 h-12 text-base focus-ring"
              autoFocus
            />
          </div>
          
          <div>
            <Label htmlFor="email" className="text-sm font-semibold">Email Address *</Label>
            <Input
              id="email"
              type="email"
              data-testid="signup-email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              placeholder="john@example.com"
              className="mt-2 h-12 text-base focus-ring"
            />
          </div>
          
          <div>
            <Label htmlFor="password" className="text-sm font-semibold">Password *</Label>
            <Input
              id="password"
              type="password"
              data-testid="signup-password"
              value={formData.password}
              onChange={handlePasswordChange}
              required
              minLength={6}
              placeholder="Minimum 6 characters"
              className="mt-2 h-12 text-base focus-ring"
            />
            {passwordStrength && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  <div className={`h-1 flex-1 rounded ${passwordStrength === 'weak' || passwordStrength === 'medium' || passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-200'}`}></div>
                  <div className={`h-1 flex-1 rounded ${passwordStrength === 'medium' || passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-200'}`}></div>
                  <div className={`h-1 flex-1 rounded ${passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-200'}`}></div>
                </div>
                <p className="text-xs text-gray-600 capitalize">{passwordStrength} password</p>
              </div>
            )}
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all duration-200" 
            disabled={loading} 
            data-testid="signup-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating Account...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Create Free Account
                <span>→</span>
              </span>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500 pt-2">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Navigation Component
const Navigation = React.memo(({ isSidebarCollapsed, setIsSidebarCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  if (!user) return null;
  
  const navGroups = user.role === 'super_admin' ? [
    {
      title: 'Administration',
      items: [
        { path: '/admin', label: 'Admin Dashboard', icon: Shield },
        { path: '/admin/users', label: 'User Management', icon: Users },
        { path: '/platform-analytics', label: 'Platform Analytics', icon: Globe },
        { path: '/content-management', label: 'CMS', icon: Layout },
        { path: '/integrations', label: 'Integrations', icon: Settings },
        { path: '/updates', label: 'Updates', icon: FileText },
        { path: '/live-variables', label: 'Live Variables', icon: Database },
        { path: '/reset-user', label: 'Reset User', icon: RefreshCw },
        { path: '/backup-restore', label: 'Backup & Restore', icon: Database }
      ]
    },
    {
      title: 'Practice Management',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
        { path: '/clients', label: 'Clients', icon: Users },
        { path: '/invoices', label: 'Invoices', icon: IndianRupee },
        { path: '/tasks', label: 'Tasks', icon: CheckSquare }
      ]
    },
    {
      title: 'Intelligence & Analytics',
      items: [
        { path: '/analytics', label: 'My Analytics', icon: BarChart3 },
        { path: '/reminder-automation', label: 'Automation Panel', icon: Bell },
        { path: '/audit-logs', label: 'Audit Logs', icon: Shield }
      ]
    }
  ] : [
    {
      title: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: BarChart3 }
      ]
    },
    {
      title: 'Client Management',
      items: [
        { path: '/clients', label: 'Clients', icon: Users },
        { path: '/services', label: 'Services', icon: FileText },
        { path: '/client-services', label: 'Client Services', icon: LinkIcon }
      ]
    },
    {
      title: 'Financial Operations',
      items: [
        { path: '/invoices', label: 'Invoices', icon: IndianRupee },
        { path: '/receipts', label: 'Receipts', icon: TrendingUp },
        { path: '/expenses', label: 'Expenses', icon: TrendingDown }
      ]
    },
    {
      title: 'Document & Communications',
      items: [
        { path: '/documents', label: 'Documents', icon: Upload, badge: '✨ AI' },
        { path: '/whatsapp', label: 'WhatsApp Manager', icon: MessageSquare },
        { path: '/portal-management', label: 'Client Portal', icon: Building2 }
      ]
    },
    {
      title: 'Compliance & Reports',
      items: [
        { path: '/gst-return-status', label: 'GST Returns', icon: FileText },
        { path: '/reminder-automation', label: 'Automation Panel', icon: Bell },
        { path: '/analytics', label: 'Analytics Dashboard', icon: BarChart3 },
        { path: '/audit-logs', label: 'Audit Logs', icon: Shield }
      ]
    },
    {
      title: 'System & Settings',
      items: [
        { path: '/staff', label: 'Staff Management', icon: Users },
        { path: '/tasks', label: 'Task Management', icon: CheckSquare },
        { path: '/task-templates', label: 'Task Templates', icon: FileText },
        { path: '/content-management', label: 'CMS', icon: Layout, adminOnly: true },
        { path: '/settings', label: 'User Settings', icon: Settings }
      ]
    }
  ];

  const ProfileAvatar = () => {
    const initials = user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
        {user.profile_photo ? (
          <img 
            src={user.profile_photo} 
            alt={user.name} 
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          initials
        )}
      </div>
    );
  };

  const ProfileDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ProfileAvatar />
        </button>
        
        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 z-20 overflow-hidden">
              <div className="p-6 bg-gradient-to-br from-orange-50 to-red-50 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <ProfileAvatar />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-gray-900 truncate">{user.name}</p>
                    <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    <p className="text-xs text-gray-500 truncate mt-1">{user.organization_name}</p>
                  </div>
                </div>
              </div>
              
              <div className="py-3">
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                >
                  <User className="w-5 h-5 mr-3" />
                  Edit Profile
                </button>
                
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                >
                  <Settings className="w-5 h-5 mr-3" />
                  Settings
                </button>
                
                <hr className="my-2 mx-4" />

                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                    // Use window.location to ensure ?switch=true is preserved
                    window.location.href = '/?switch=true';
                  }}
                  className="flex items-center w-full px-6 py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                  data-testid="switch-account-btn"
                >
                  <RefreshCw className="w-5 h-5 mr-3" />
                  Switch Account
                </button>
                
                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-6 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="sign-out-btn"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Sign Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Top Header Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-30">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Left side - Logo and Menu Toggle */}
          <div className="flex items-center">
            {/* Desktop Sidebar Toggle */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="hidden md:flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mr-4"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mr-4"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_gst-helper-3/artifacts/58t5zxar_Screenshot_2025-10-09-13-25-10-809_com.android.chrome-edit.jpg" 
                alt="GST ASSIST Logo"
                className="w-10 h-10 mr-2 rounded-lg"
              />
              {!isSidebarCollapsed && (
                <div>
                  <h1 className="text-base font-bold text-gray-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
                    GST ASSIST<sup style={{fontSize: '0.65em', marginLeft: '2px', fontWeight: 'normal'}}>™</sup>
                  </h1>
                  {user.role === 'super_admin' && (
                    <Badge variant="destructive" className="text-xs">Super Admin</Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Profile */}
          <div className="flex items-center space-x-3">
            <div className="hidden sm:block text-sm text-gray-600 text-right">
              <span className="font-medium">{user.name}</span>
              <br />
              <span className="text-xs">{user.organization_name}</span>
            </div>
            <ProfileDropdown />
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex fixed top-16 left-0 h-full bg-white shadow-lg border-r border-gray-200 transition-all duration-300 z-20 ${
        isSidebarCollapsed ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col w-full">
          <div className="flex-1 py-4 overflow-y-auto">
            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-6">
                {!isSidebarCollapsed && (
                  <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {group.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    // Show admin-only items only to super admin
                    if (item.adminOnly && user?.role !== 'super_admin') {
                      return null;
                    }
                    
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        title={isSidebarCollapsed ? item.label : ''}
                        className={`flex items-center px-4 py-3 text-sm font-semibold transition-all duration-200 rounded-lg mx-2 ${
                          isActive
                            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <Icon className={`${isSidebarCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} ${isActive ? 'text-white' : ''}`} />
                        {!isSidebarCollapsed && (
                          <>
                            <span className="truncate">{item.label}</span>
                            {item.badge && (
                              <Badge className="ml-auto text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 px-2 py-1 font-semibold">
                                {item.badge}
                              </Badge>
                            )}
                            {item.adminOnly && (
                              <Badge variant="destructive" className="ml-auto text-xs font-semibold">Admin</Badge>
                            )}
                          </>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          {/* Sidebar Footer */}
          <div className="border-t border-gray-200 p-4">
            <Link
              to="/profile"
              className={`flex items-center py-2 text-sm text-gray-600 hover:text-gray-900 ${
                isSidebarCollapsed ? 'justify-center' : ''
              }`}
              title={isSidebarCollapsed ? 'Edit Profile' : ''}
            >
              <User className={`${isSidebarCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-3'}`} />
              {!isSidebarCollapsed && <span>Edit Profile</span>}
            </Link>
            <Link
              to="/settings"
              className={`flex items-center py-2 text-sm text-gray-600 hover:text-gray-900 ${
                isSidebarCollapsed ? 'justify-center' : ''
              }`}
              title={isSidebarCollapsed ? 'Settings' : ''}
            >
              <Settings className={`${isSidebarCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-3'}`} />
              {!isSidebarCollapsed && <span>Settings</span>}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}></div>
          
          {/* Mobile Sidebar */}
          <div className="fixed top-16 left-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
            {/* Profile Section Mobile */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <ProfileAvatar />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.organization_name}</p>
                  {user.role === 'super_admin' && (
                    <Badge variant="destructive" className="mt-1">Super Admin</Badge>
                  )}
                </div>
              </div>
            </div>
            
            {/* Navigation Groups Mobile */}
            <div className="py-4 overflow-y-auto" style={{ height: 'calc(100vh - 200px)' }}>
              {navGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-6">
                  <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {group.title}
                  </h3>
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      // Show admin-only items only to super admin
                      if (item.adminOnly && user?.role !== 'super_admin') {
                        return null;
                      }
                      
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center px-4 py-3 text-base font-medium transition-colors duration-200 ${
                            location.pathname === item.path
                              ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <Icon className="w-5 h-5 mr-3" />
                          {item.label}
                          {item.adminOnly && (
                            <Badge variant="destructive" className="ml-auto text-xs">Admin</Badge>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Profile Actions Mobile */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
              <Button 
                variant="outline" 
                className="w-full justify-start mb-2" 
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
              >
                <User className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start mb-2" 
                onClick={() => {
                  navigate('/settings');
                  setIsMobileMenuOpen(false);
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start mb-2 text-blue-600 border-blue-200 hover:bg-blue-50" 
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                  window.location.href = '/?switch=true';
                }}
                data-testid="mobile-switch-account-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Switch Account
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                data-testid="mobile-sign-out-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

// Date Picker Component - DISABLED FOR INVOICES PAGE
const DatePicker = ({ date, onDateChange, placeholder = "Pick a date" }) => {
  // Return a simple input field instead of calendar for now
  return (
    <input
      type="date"
      value={date ? date.toISOString().split('T')[0] : ''}
      onChange={(e) => onDateChange(e.target.value ? new Date(e.target.value) : null)}
      className="w-full p-2 border border-gray-300 rounded-md"
    />
  );
};

// Circular Progress Component
const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = "#ff6b35" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-800">{percentage}%</span>
      </div>
    </div>
  );
};

// Dashboard Component with Stunning UI

// New Direct Data Cards Component - Fetches fresh data directly from backend
const DirectDataCards = ({ filterPeriod, selectedMonth, handleDrillDown }) => {
  const [cardData, setCardData] = useState({
    total_clients: 0,
    total_invoiced: 0,
    receipts: 0,
    outstanding: 0,
    invoice_count: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchDirectData = async () => {
    try {
      setLoading(true);
      
      // Build API params same as dashboard
      const params = {
        period: filterPeriod,
        _t: Date.now() // Cache buster
      };
      
      if (filterPeriod === 'month') {
        params.month = selectedMonth.month;
        params.year = selectedMonth.year;
      }

      console.log('🔄 DirectDataCards fetching with params:', params);

      // Fetch dashboard stats directly
      const response = await axios.get(`${API}/dashboard/stats`, { 
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      console.log('📊 DirectDataCards received data:', response.data);

      setCardData({
        total_clients: response.data.total_clients || 0,
        total_invoiced: response.data.total_invoiced || response.data.total_invoice_amount || 0,
        receipts: response.data.receipts || response.data.total_receipts || 0,
        outstanding: response.data.outstanding || response.data.total_due || 0,
        invoice_count: response.data.invoice_count || 0
      });

    } catch (error) {
      console.error('❌ Error fetching direct card data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectData();
  }, [filterPeriod, selectedMonth]);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString()}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Clients */}
      <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl group"
        onClick={() => handleDrillDown('/clients')}
      >
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide">Total Clients</p>
              <p className="text-4xl font-black mt-3">{loading ? '...' : cardData.total_clients}</p>
              <div className="flex items-center mt-3 opacity-90 group-hover:opacity-100 transition-opacity">
                <Users className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Click to view all →</span>
              </div>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl group-hover:bg-white/30 transition-colors group-hover:scale-110 transition-transform">
              <Users className="w-10 h-10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Invoiced - DIRECT FROM API */}
      <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl group"
        onClick={() => handleDrillDown('/invoices')}
      >
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-semibold uppercase tracking-wide">Total Invoiced</p>
              <p className="text-4xl font-black mt-3">{loading ? '...' : formatCurrency(cardData.total_invoiced)}</p>
              <div className="flex items-center mt-3 opacity-90 group-hover:opacity-100 transition-opacity">
                <FileText className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{cardData.invoice_count} invoices →</span>
              </div>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl group-hover:bg-white/30 transition-colors group-hover:scale-110 transition-transform">
              <FileText className="w-10 h-10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipts - DIRECT FROM API */}
      <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl group"
        onClick={() => handleDrillDown('/receipts')}
      >
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-semibold uppercase tracking-wide">Total Receipts</p>
              <p className="text-4xl font-black mt-3">{loading ? '...' : formatCurrency(cardData.receipts)}</p>
              <div className="flex items-center mt-3 opacity-90 group-hover:opacity-100 transition-opacity">
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Payments received →</span>
              </div>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <CreditCard className="w-8 h-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outstanding - DIRECT FROM API */}
      <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
        onClick={() => handleDrillDown('/invoices', { status_filter: 'unpaid' })}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Outstanding (DIRECT)</p>
              <p className="text-3xl font-bold mt-2">{loading ? '...' : formatCurrency(cardData.outstanding)}</p>
              <div className="flex items-center mt-2">
                <AlertCircle className="w-4 h-4 mr-1" />
                <span className="text-sm text-orange-100">Amount pending</span>
              </div>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <AlertCircle className="w-8 h-8" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [stats, setStats] = useState({
    total_revenue: 0,
    outstanding_amount: 0,
    whatsapp_stats: {
      api_messages_sent: 0,
      api_limit: 500,
      api_used: 0,
      api_balance: 500,
      webapp_messages_sent: 0
    }
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to previous month for better user experience
    const now = new Date();
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { 
      month: previousMonth.getMonth() + 1, 
      year: previousMonth.getFullYear() 
    };
  });
  const [filterPeriod, setFilterPeriod] = useState('month'); // Default to month to show previous month data

  // Check if user needs onboarding
  useEffect(() => {
    if (user && user.role !== 'super_admin' && !user.onboarding_completed) {
      setShowOnboarding(true);
    }
  }, [user]);

  // Main dashboard data fetcher - single source of truth
  const [dashboardData, setDashboardData] = useState({
    total_clients: 0,
    total_invoiced: 0,
    receipts: 0,
    outstanding: 0,
    invoice_count: 0,
    total_tax_collections: 0,
    total_tax_payments: 0,
    total_expenses: 0,
    total_profit: 0
  });
  const [gstStats, setGstStats] = useState(null);
  const [showGstDrillDown, setShowGstDrillDown] = useState(false);
  const [gstDrillDownData, setGstDrillDownData] = useState([]);
  const [gstDrillDownParams, setGstDrillDownParams] = useState({});
  const [incompleteClients, setIncompleteClients] = useState([]);
  const [updatingClients, setUpdatingClients] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ show: false, current: 0, total: 0, message: '' });

  const checkIncompleteClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      const clients = response.data;
      const incomplete = clients.filter(c => c.gst_enabled && c.gstin && !c.gst_portal_data);
      setIncompleteClients(incomplete);
    } catch (error) {
      console.error('Error checking incomplete clients:', error);
    }
  };

  const handleBulkUpdateClients = async () => {
    if (incompleteClients.length === 0) {
      alert('No clients need updating!');
      return;
    }

    setShowBulkUpdateModal(false);
    setUpdatingClients(true);
    setUpdateProgress({ show: true, current: 0, total: incompleteClients.length, message: 'Starting update...' });
    
    let success = 0;
    let failed = 0;

    try {
      for (let i = 0; i < incompleteClients.length; i++) {
        const client = incompleteClients[i];
        
        setUpdateProgress({ 
          show: true, 
          current: i + 1, 
          total: incompleteClients.length, 
          message: `Fetching GST details for ${client.name}...` 
        });
        
        try {
          // Fetch GSTIN data
          const gstResponse = await axios.get(`${API}/clients/fetch-gstin/${client.gstin}`);
          const gstData = gstResponse.data;

          // Update client with GST portal data
          await axios.put(`${API}/clients/${client.id}`, {
            ...client,
            organization_name: gstData.trade_name || gstData.legal_name || client.organization_name,
            name: gstData.legal_name || client.name,
            state: gstData.state || client.state,
            address: gstData.address || client.address,
            city: gstData.city || client.city,
            pincode: gstData.pincode || client.pincode,
            registration_type: gstData.registration_type || client.registration_type,
            state_jurisdiction: gstData.state_jurisdiction || client.state_jurisdiction,
            central_jurisdiction: gstData.central_jurisdiction || client.central_jurisdiction,
            constitution: gstData.constitution || client.constitution,
            registration_date: gstData.registration_date || client.registration_date,
            einvoice_status: gstData.einvoice_status || client.einvoice_status,
            gst_portal_data: gstData.raw_gst_data
          });

          success++;
          
          // Small delay for visual feedback
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Failed to update client ${client.name}:`, error);
          failed++;
        }
      }

      setUpdateProgress({ show: true, current: incompleteClients.length, total: incompleteClients.length, message: 'Update complete!' });
      
      setTimeout(() => {
        setUpdateProgress({ show: false, current: 0, total: 0, message: '' });
        alert(`✅ Bulk Update Complete!\n\n✅ Successfully updated: ${success} clients\n${failed > 0 ? `❌ Failed: ${failed} clients` : ''}\n\n💡 API credits consumed: ${success}`);
      }, 1000);
      
      // Refresh incomplete clients list
      await checkIncompleteClients();
    } catch (error) {
      setUpdateProgress({ show: false, current: 0, total: 0, message: '' });
      alert('Error during bulk update: ' + error.message);
    } finally {
      setUpdatingClients(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const params = {
        period: filterPeriod
      };
      
      if (filterPeriod === 'month') {
        params.month = selectedMonth.month;
        params.year = selectedMonth.year;
      }

      console.log('🔄 Fetching dashboard data with params:', params);

      const response = await axios.get(`${API}/dashboard/stats`, { params });

      console.log('📊 Dashboard data received:', response.data);

      setDashboardData({
        total_clients: response.data.total_clients || 0,
        total_invoiced: response.data.total_invoiced || response.data.total_invoice_amount || 0,
        receipts: response.data.receipts || response.data.total_receipts || 0,
        outstanding: response.data.outstanding || response.data.total_due || 0,
        invoice_count: response.data.invoice_count || 0,
        total_tax_collections: response.data.total_tax_collections || 0,
        total_tax_payments: response.data.total_tax_payments || 0,
        total_expenses: response.data.total_expenses || 0,
        total_profit: response.data.total_profit || 0
      });

    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
    }
  };

  const fetchGstStats = async () => {
    try {
      // Determine financial year based on selected month
      const month = selectedMonth.month;
      const year = selectedMonth.year;
      let financialYear;
      
      if (month >= 4) {
        financialYear = `${year}-${(year + 1) % 100}`;
      } else {
        financialYear = `${year - 1}-${year % 100}`;
      }
      
      const response = await axios.get(`${API}/dashboard/gst-stats`, {
        params: {
          financial_year: financialYear,
          month: month
        }
      });
      
      setGstStats(response.data);
    } catch (error) {
      console.error('Error fetching GST stats:', error);
      setGstStats(null);
    }
  };

  const handleGstDrillDown = async (returnType, status) => {
    try {
      const month = selectedMonth.month;
      const year = selectedMonth.year;
      let financialYear;
      
      if (month >= 4) {
        financialYear = `${year}-${(year + 1) % 100}`;
      } else {
        financialYear = `${year - 1}-${year % 100}`;
      }
      
      const response = await axios.get(`${API}/dashboard/gst-clients-detail`, {
        params: {
          financial_year: financialYear,
          month: month,
          return_type: returnType,
          status_filter: status
        }
      });
      
      setGstDrillDownData(response.data);
      setGstDrillDownParams({
        returnType,
        status,
        month,
        year,
        financialYear
      });
      setShowGstDrillDown(true);
    } catch (error) {
      console.error('Error fetching GST drill-down data:', error);
      alert('Error fetching client details');
    }
  };

  const exportGstDrillDownToExcel = () => {
    // Convert data to Excel format
    const exportData = gstDrillDownData.map(client => ({
      'Client Name': client.clientName,
      'GSTIN': client.gstin,
      'Overall Status': client.overallStatus,
      'Filed Return Types': client.filedReturnTypes.join(', ') || 'None',
      'Not Filed Return Types': client.notFiledReturnTypes.join(', ') || 'None',
      'Filing Details': client.filingDetails.map(fd => 
        `${fd.returnType}: ${fd.status} (${fd.filingDate || 'Not Filed'})`
      ).join(' | ')
    }));

    // Create CSV
    const headers = Object.keys(exportData[0]);
    const csv = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const monthName = new Date(2025, gstDrillDownParams.month - 1).toLocaleString('default', { month: 'long' });
    a.download = `GST_${gstDrillDownParams.returnType}_${gstDrillDownParams.status}_${monthName}_${gstDrillDownParams.year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCompleteOnboarding = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`);
      setShowOnboarding(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const handleSkipOnboarding = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`);
      setShowOnboarding(false);
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      setShowOnboarding(false);
    }
  };

  useEffect(() => {
    // Fetch data in parallel for faster loading
    Promise.all([
      fetchDashboardData(),
      fetchGstStats()
    ]);
    checkIncompleteClients();
  }, [filterPeriod, selectedMonth]);

  const DirectDataCards = () => {

    const formatCurrency = (amount) => {
      return `₹${(amount || 0).toLocaleString()}`;
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleDrillDown('/clients')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Clients</p>
                <p className="text-3xl font-bold mt-2">{dashboardData.total_clients}</p>
                <div className="flex items-center mt-2">
                  <Users className="w-4 h-4 mr-1" />
                  <span className="text-sm text-blue-100">Click to view clients</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <Users className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GST Statistics Card */}
        {gstStats && gstStats.totalRegularClients > 0 && (
          <Card className="bg-gradient-to-br from-green-500 to-teal-600 text-white border-0 shadow-xl">
            <CardContent className="p-6">
              <div>
                <p className="text-green-100 text-sm font-medium mb-2">GST Regular Clients</p>
                <p className="text-3xl font-bold">{gstStats.totalRegularClients}</p>
                
                <div className="mt-4 space-y-2">
                  {gstStats.returnTypes.map((rt, idx) => (
                    <div key={idx} className="bg-white/10 rounded-lg p-2">
                      <div className="text-xs font-semibold mb-1">{rt.returnType}</div>
                      <div className="flex justify-between text-sm">
                        <span 
                          className="cursor-pointer hover:underline flex items-center"
                          onClick={async () => {
                            await handleGstDrillDown(rt.returnType, 'filed');
                          }}
                        >
                          ✅ Filed: {rt.filed}
                        </span>
                        <span 
                          className="cursor-pointer hover:underline flex items-center"
                          onClick={async () => {
                            await handleGstDrillDown(rt.returnType, 'not_filed');
                          }}
                        >
                          ❌ Not Filed: {rt.notFiled}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleDrillDown('/invoices')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Invoiced</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.total_invoiced)}</p>
                <div className="flex items-center mt-2">
                  <FileText className="w-4 h-4 mr-1" />
                  <span className="text-sm text-purple-100">{dashboardData.invoice_count} invoices</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <FileText className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleDrillDown('/receipts')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Receipts</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.receipts)}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm text-green-100">Payments received</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <CreditCard className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleDrillDown('/invoices', { status_filter: 'unpaid' })}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Outstanding</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.outstanding)}</p>
                <div className="flex items-center mt-2">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  <span className="text-sm text-orange-100">Amount pending</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <AlertCircle className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const DirectTaxCards = () => {
    const formatCurrency = (amount) => {
      return `₹${(amount || 0).toLocaleString()}`;
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Tax Collections</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.total_tax_collections)}</p>
                <div className="flex items-center mt-2">
                  <FileText className="w-4 h-4 mr-1" />
                  <span className="text-sm text-purple-100">GST Collected</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <FileText className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">Tax Payments</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.total_tax_payments)}</p>
                <div className="flex items-center mt-2">
                  <IndianRupee className="w-4 h-4 mr-1" />
                  <span className="text-sm text-indigo-100">GST Paid</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <IndianRupee className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0 shadow-xl cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleDrillDown('/expenses')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Expenses</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.total_expenses)}</p>
                <div className="flex items-center mt-2">
                  <TrendingDown className="w-4 h-4 mr-1" />
                  <span className="text-sm text-red-100">Click to view</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <TrendingDown className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Total Profit</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.total_profit)}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm text-emerald-100">Net Earnings</span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <TrendingUp className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>


      {/* GST Drill-Down Modal */}
      {showGstDrillDown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {gstDrillDownParams.returnType} - {gstDrillDownParams.status === 'filed' ? 'Filed' : 'Not Filed'} Clients
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(2025, gstDrillDownParams.month - 1).toLocaleString('default', { month: 'long' })} {gstDrillDownParams.year} (FY: {gstDrillDownParams.financialYear})
                </p>
              </div>
              <button
                onClick={() => setShowGstDrillDown(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {gstDrillDownData.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No clients found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Client Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">GSTIN</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Filing Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {gstDrillDownData.map((client, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{client.clientName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{client.gstin}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              client.overallStatus === 'Filed' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {client.overallStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {client.filingDetails.length > 0 ? (
                              <div className="space-y-1">
                                {client.filingDetails.map((fd, fdIdx) => (
                                  <div key={fdIdx}>
                                    <span className="font-medium">{fd.returnType}:</span> {fd.status}
                                    {fd.filingDate && <span className="text-gray-500"> ({new Date(fd.filingDate).toLocaleDateString()})</span>}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">No filing data</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total: {gstDrillDownData.length} clients
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportGstDrillDownToExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export to Excel
                </button>
                <button
                  onClick={() => setShowGstDrillDown(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    );
  };

  // Drill-down navigation functions
  const handleDrillDown = (page, filterData = {}) => {
    // Store the current dashboard filter state for drill-down context
    const dashboardContext = {
      period: filterPeriod,
      month: filterPeriod === 'month' ? selectedMonth.month : null,
      year: filterPeriod === 'month' ? selectedMonth.year : null,
      ...filterData
    };
    
    // Navigate to the page with context
    navigate(page, { state: dashboardContext });
  };
  
  // WhatsApp Image Upload States (for modal compatibility)
  const [showWhatsappImageModal, setShowWhatsappImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSendingImage, setIsSendingImage] = useState(false);
  const [invoice, setInvoice] = useState(null);
  
  // Removed old fetchStats to prevent duplicate API calls and flickering
  // All data is now fetched by DirectDataCards and DirectTaxCards components
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // WhatsApp image functionality (placeholder for Dashboard compatibility)
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageViaWhatsApp = async () => {
    alert('This feature is available in the Invoices section');
    setShowWhatsappImageModal(false);
    setImageFile(null);
    setImagePreview(null);
  };

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const collectionRate = (stats.total_revenue || 0) > 0 ? Math.round(((stats.total_revenue || 0) / ((stats.total_revenue || 0) + (stats.outstanding_amount || 0))) * 100) : 0;
  const monthlyTarget = 50000; // Example target
  const monthlyProgress = Math.min(Math.round(((stats.total_revenue || 0) / monthlyTarget) * 100), 100);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Onboarding Wizard */}
      {showOnboarding && user && user.role !== 'super_admin' && (
        <OnboardingWizard 
          user={user}
          onComplete={handleCompleteOnboarding}
          onSkip={handleSkipOnboarding}
        />
      )}

      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Welcome back, {user?.name?.split(' ')[0]} 👋
              </h1>
              <p className="text-gray-600 mt-1">Here's what's happening with your practice today</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Today</div>
                <div className="text-lg font-bold">
                  {currentDate.toLocaleDateString('en-IN', { 
                    day: 'numeric', 
                    month: 'short',
                    weekday: 'short'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Filter Controls */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-600" />
                <span className="font-semibold text-gray-700">Filter by:</span>
              </div>
              
              <Select value={filterPeriod} onValueChange={(value) => {
                console.log('🎛️ Filter period changed to:', value);
                setFilterPeriod(value);
              }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overall">Overall</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
              
              {filterPeriod === 'month' && (
                <>
                  <Select 
                    value={selectedMonth.month.toString()} 
                    onValueChange={(value) => {
                      console.log('🗓️ Month changed to:', value);
                      setSelectedMonth({...selectedMonth, month: parseInt(value)});
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="2">February</SelectItem>
                      <SelectItem value="3">March</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="5">May</SelectItem>
                      <SelectItem value="6">June</SelectItem>
                      <SelectItem value="7">July</SelectItem>
                      <SelectItem value="8">August</SelectItem>
                      <SelectItem value="9">September</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">December</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={selectedMonth.year.toString()} 
                    onValueChange={(value) => {
                      console.log('📅 Year changed to:', value);
                      setSelectedMonth({...selectedMonth, year: parseInt(value)});
                    }}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 5}, (_, i) => {
                        const year = new Date().getFullYear() - 2 + i;
                        return (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDashboardData}
                className="ml-auto flex items-center gap-2 hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Incomplete Client Data Notification */}
        {incompleteClients.length > 0 && (
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-500 p-2 rounded-full">
                      <AlertCircle className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-yellow-900">
                      Client Information Incomplete
                    </h3>
                  </div>
                  <p className="text-yellow-800 mb-4">
                    {incompleteClients.length} GST-enabled {incompleteClients.length === 1 ? 'client' : 'clients'} {incompleteClients.length === 1 ? 'does' : 'do'} not have complete information from GST Portal. 
                    Update to get legal name, business type, address and more details.
                  </p>
                  
                  {/* Progress Bar */}
                  {updateProgress.show && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2 text-center">
                        🔄 Updating Client Information
                      </h4>
                      <div className="mb-2">
                        <div className="flex justify-between text-sm text-blue-700 mb-2">
                          <span>{updateProgress.message}</span>
                          <span className="font-semibold">{updateProgress.current} / {updateProgress.total}</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-4 overflow-hidden shadow-inner">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out flex items-center justify-end pr-2"
                            style={{ width: `${(updateProgress.current / updateProgress.total) * 100}%` }}
                          >
                            <span className="text-white text-xs font-bold">
                              {Math.round((updateProgress.current / updateProgress.total) * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-blue-600 text-center animate-pulse">
                        Fetching GST portal data...
                      </p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={() => setShowBulkUpdateModal(true)}
                      disabled={updatingClients}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {updatingClients ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Updating...
                        </>
                      ) : (
                        <>
                          🔄 Update Client Information from GST Portal
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-yellow-700 flex items-center">
                      💡 Will consume {incompleteClients.length} API {incompleteClients.length === 1 ? 'credit' : 'credits'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk Update Confirmation Modal */}
        <Dialog open={showBulkUpdateModal} onOpenChange={setShowBulkUpdateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Client Information from GST Portal?</DialogTitle>
              <DialogDescription>
                This will fetch complete GST details for {incompleteClients.length} {incompleteClients.length === 1 ? 'client' : 'clients'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">What will be fetched:</h4>
                <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
                  <li>Legal Name & Trade Name</li>
                  <li>Business Type & Taxpayer Type</li>
                  <li>Complete Address (State, City, Pincode)</li>
                  <li>Registration Date & Status</li>
                  <li>Nature of Business</li>
                </ul>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">⚠️ API Credits:</h4>
                <p className="text-sm text-yellow-800">
                  This operation will consume <strong>{incompleteClients.length} API credits</strong> from Public Search GST API.
                  Each client fetch = 1 credit.
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleBulkUpdateClients}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Yes, Update All Clients
                </Button>
                <Button 
                  onClick={() => setShowBulkUpdateModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* NEW CORRECTED Financial Overview Cards */}
        <DirectDataCards />

        {/* Duplicate section removed */}

        {/* NEW CORRECTED Tax & Financial Cards */}
        <DirectTaxCards />

        {/* WhatsApp Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* WhatsApp API Messages */}
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-green-100 text-sm font-medium">WhatsApp API Messages</p>
                  <p className="text-3xl font-bold mt-2">{stats.whatsapp_stats?.api_messages_sent || 0}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-full">
                  <MessageCircle className="w-8 h-8" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">Available</span>
                  <span className="font-medium text-white">{stats.whatsapp_stats?.api_limit || 500}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">Used</span>
                  <span className="font-medium text-white">{stats.whatsapp_stats?.api_used || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">Balance</span>
                  <span className="font-medium text-green-200">{stats.whatsapp_stats?.api_balance || 500}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp WebApp Messages */}
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white border-0 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">WhatsApp WebApp Messages</p>
                  <p className="text-3xl font-bold mt-2">{stats.whatsapp_stats?.webapp_messages_sent || 0}</p>
                  <div className="flex items-center mt-2">
                    <Send className="w-4 h-4 mr-1" />
                    <span className="text-sm text-blue-100">Sent via Web</span>
                  </div>
                </div>
                <div className="bg-white/20 p-3 rounded-full">
                  <Send className="w-8 h-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics & Progress Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Collection Performance */}
          <Card className="bg-white shadow-xl border-0 hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">Collection Rate</CardTitle>
              <p className="text-gray-600 text-sm">Revenue collection efficiency</p>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8">
              <CircularProgress 
                percentage={collectionRate} 
                size={140} 
                color="#ff6b35"
              />
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">Monthly Performance</p>
                <div className="flex items-center justify-center mt-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">Excellent Collection</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Target */}
          <Card className="bg-white shadow-xl border-0 hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">Monthly Target</CardTitle>
              <p className="text-gray-600 text-sm">₹{monthlyTarget.toLocaleString()} goal</p>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8">
              <CircularProgress 
                percentage={monthlyProgress} 
                size={140} 
                color="#10b981"
              />
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">Target Achievement</p>
                <div className="flex items-center justify-center mt-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">On Track</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold">Quick Actions</CardTitle>
              <p className="text-indigo-100 text-sm">Streamline your workflow</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Link to="/clients" className="block">
                <div className="bg-white/20 hover:bg-white/30 transition-all duration-200 p-4 rounded-xl cursor-pointer">
                  <div className="flex items-center">
                    <div className="bg-white/20 p-2 rounded-lg mr-3">
                      <Plus className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Add New Client</p>
                      <p className="text-sm text-indigo-100">Expand your practice</p>
                    </div>
                  </div>
                </div>
              </Link>
              
              <Link to="/communications" className="block">
                <div className="bg-white/20 hover:bg-white/30 transition-all duration-200 p-4 rounded-xl cursor-pointer">
                  <div className="flex items-center">
                    <div className="bg-white/20 p-2 rounded-lg mr-3">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">WhatsApp Clients</p>
                      <p className="text-sm text-indigo-100">Send updates & reminders</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/invoices" className="block">
                <div className="bg-white/20 hover:bg-white/30 transition-all duration-200 p-4 rounded-xl cursor-pointer">
                  <div className="flex items-center">
                    <div className="bg-white/20 p-2 rounded-lg mr-3">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Generate Invoice</p>
                      <p className="text-sm text-indigo-100">Create GST bills</p>
                    </div>
                  </div>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity & Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Business Insights */}
          <Card className="bg-white shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">Business Insights</CardTitle>
              <p className="text-gray-600 text-sm">Key performance indicators</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mr-4">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Revenue Growth</p>
                    <p className="text-sm text-gray-600">Monthly increase</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">+12.5%</p>
                  <p className="text-sm text-gray-500">vs last month</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Client Retention</p>
                    <p className="text-sm text-gray-600">Customer loyalty</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">98%</p>
                  <p className="text-sm text-gray-500">retention rate</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">GST Compliance</p>
                    <p className="text-sm text-gray-600">Filing accuracy</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">100%</p>
                  <p className="text-sm text-gray-500">on-time filing</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card className="bg-white shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">System Status</CardTitle>
              <p className="text-gray-600 text-sm">Platform health & connectivity</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-semibold text-gray-800">Database</p>
                    <p className="text-sm text-gray-600">All systems operational</p>
                  </div>
                </div>
                <span className="text-green-600 font-semibold">Online</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-semibold text-gray-800">GST Portal</p>
                    <p className="text-sm text-gray-600">Connected & synced</p>
                  </div>
                </div>
                <span className="text-green-600 font-semibold">Connected</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-semibold text-gray-800">WhatsApp API</p>
                    <p className="text-sm text-gray-600">Ready for messaging</p>
                  </div>
                </div>
                <span className="text-orange-600 font-semibold">Ready</span>
              </div>

              <div className="mt-8 p-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl text-white">
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-2">Need Help? 🤝</h3>
                  <p className="text-sm text-indigo-100 mb-4">
                    Our support team is here to assist you with any questions about GST compliance or client management.
                  </p>
                  <Button variant="secondary" size="sm" className="bg-white text-indigo-600 hover:bg-gray-100">
                    Contact Support
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Image Upload Modal */}
      {showWhatsappImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Image via WhatsApp</h3>
                <button
                  onClick={() => {
                    setShowWhatsappImageModal(false);
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>To:</strong> {invoice.client_name}</p>
                  <p><strong>Mobile:</strong> {invoice.client_mobile}</p>
                  <p><strong>Invoice:</strong> {invoice.invoice_number}</p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={imagePreview} 
                        alt="Selected" 
                        className="max-w-full max-h-48 mx-auto rounded"
                      />
                      <div className="text-sm text-gray-600">
                        {imageFile?.name} ({(imageFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                      <button
                        onClick={() => document.getElementById('whatsapp-image-input').click()}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Choose different image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-gray-600 mb-2">Select an image to send with the invoice</p>
                        <button
                          onClick={() => document.getElementById('whatsapp-image-input').click()}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Select Image
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Max size: 5MB. Supported: JPG, PNG, GIF</p>
                    </div>
                  )}
                  
                  <input
                    id="whatsapp-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowWhatsappImageModal(false);
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={isSendingImage}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendImageViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!imageFile || isSendingImage}
                  >
                    {isSendingImage ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
 
    </div>
  );
};

// Generate Invoices Button Component
const GenerateInvoicesButton = ({ onGenerated }) => {
  const [loading, setLoading] = useState(false);
  
  const handleGenerateInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate-invoices`);
      alert(response.data.message);
      if (onGenerated) onGenerated();
    } catch (error) {
      console.error('Error generating invoices:', error);
      alert('Error generating invoices');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Button 
      onClick={handleGenerateInvoices} 
      disabled={loading}
      className="w-full justify-start"
      variant="outline"
    >
      <FileText className="w-4 h-4 mr-2" />
      {loading ? 'Generating...' : 'Generate Invoices'}
    </Button>
  );
};

// Include all the remaining components from the previous implementation
// (Clients, Services, Invoices, Payments components go here)

// Super Admin Dashboard
const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAdminData();
  }, []);
  
  const fetchAdminData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const updateUserLicense = async (userId, status, expiryDate = null) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/license`, {
        license_status: status,
        license_expiry: expiryDate
      });
      fetchAdminData(); // Refresh data
    } catch (error) {
      console.error('Error updating license:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="bg-white shadow-lg">
              <CardContent className="p-6 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="h-96 bg-gray-200 rounded-xl animate-pulse"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Super Admin Dashboard</h2>
      
      {/* Stats Cards - Enhanced */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-blue-100 text-xs font-semibold uppercase tracking-wide">Total Users</p>
              <Users className="h-5 w-5 text-blue-100" />
            </div>
            <div className="text-3xl font-black">{stats?.total_users || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-green-100 text-xs font-semibold uppercase tracking-wide">Active Users</p>
              <Users className="h-5 w-5 text-green-100" />
            </div>
            <div className="text-3xl font-black">{stats?.active_users || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-red-100 text-xs font-semibold uppercase tracking-wide">Inactive Users</p>
              <Users className="h-5 w-5 text-red-100" />
            </div>
            <div className="text-3xl font-black">{stats?.inactive_users || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-purple-100 text-xs font-semibold uppercase tracking-wide">Total Clients</p>
              <FileText className="h-5 w-5 text-purple-100" />
            </div>
            <div className="text-3xl font-black">{stats?.total_clients || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-yellow-100 text-xs font-semibold uppercase tracking-wide">Total Invoices</p>
              <IndianRupee className="h-5 w-5 text-yellow-100" />
            </div>
            <div className="text-3xl font-black">{stats?.total_invoices || 0}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Manage user licenses and view statistics</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Clients</TableHead>
                <TableHead>Invoices</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>{user.organization_name}</TableCell>
                  <TableCell>{user.client_count}</TableCell>
                  <TableCell>{user.invoice_count}</TableCell>
                  <TableCell>₹{user.total_revenue?.toLocaleString() || 0}</TableCell>
                  <TableCell>
                    <Badge variant={user.license_status === 'active' ? 'default' : 'destructive'}>
                      {user.license_status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {user.license_status === 'active' ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateUserLicense(user.user_id, 'inactive')}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => updateUserLicense(user.user_id, 'active')}
                        >
                          Activate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Clients Component
const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGST, setFilterGST] = useState('all');
  const navigate = useNavigate();
  
  // WhatsApp Image Upload States (for modal compatibility)
  const [showWhatsappImageModal, setShowWhatsappImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSendingImage, setIsSendingImage] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organization_name: '',
    gstin: '',
    pan: '',
    email: '',
    mobile: '',
    whatsapp: '',
    gstin_username: '',
    gstin_password: '',
    ewaybill_username: '',
    ewaybill_password: '',
    billing_start_date: new Date(),
    gst_enabled: false,
    state: '',
    address: '',
    city: '',
    pincode: '',
    registration_type: '',
    state_jurisdiction: '',
    central_jurisdiction: '',
    constitution: '',
    registration_date: '',
    einvoice_status: '',
    gst_portal_data: null
  });
  
  useEffect(() => {
    fetchClients();
  }, []);
  
  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSyncGSTIN = async () => {
    if (!formData.gstin || formData.gstin.length !== 15) {
      alert('Please enter a valid 15-digit GSTIN first');
      return;
    }
    
    setAutoSyncing(true);
    try {
      const response = await axios.get(`${API}/clients/fetch-gstin/${formData.gstin}`);
      const gstData = response.data;
      
      // Map fields correctly:
      // - Client name (individual contact) = Legal Name from API
      // - Organization name (business) = Trade Name from API
      setFormData({
        ...formData,
        name: gstData.legal_name || formData.name,  // Legal Name -> Client Name
        organization_name: gstData.trade_name || gstData.legal_name || formData.organization_name,  // Trade Name -> Organization
        state: gstData.state || formData.state,
        address: gstData.address || formData.address,
        city: gstData.city || formData.city,
        pincode: gstData.pincode || formData.pincode,
        registration_type: gstData.registration_type || formData.registration_type,
        state_jurisdiction: gstData.state_jurisdiction || formData.state_jurisdiction,
        central_jurisdiction: gstData.central_jurisdiction || formData.central_jurisdiction,
        constitution: gstData.constitution || formData.constitution,
        registration_date: gstData.registration_date || formData.registration_date,
        einvoice_status: gstData.einvoice_status || formData.einvoice_status,
        gst_enabled: true,
        gst_portal_data: gstData.raw_gst_data
      });
      
      alert('✅ GSTIN details fetched successfully!\n\n📋 Mapped:\n• Client Name ← Legal Name\n• Organization ← Trade Name\n• Registration Type, Jurisdiction, Constitution\n• Address details auto-filled');
    } catch (error) {
      console.error('Error fetching GSTIN details:', error);
      alert('❌ Failed to fetch GSTIN details. Please check the GSTIN and try again.');
    } finally {
      setAutoSyncing(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGST = filterGST === 'all' || 
                      (filterGST === 'enabled' && client.gst_enabled) ||
                      (filterGST === 'disabled' && !client.gst_enabled);
    
    return matchesSearch && matchesGST;
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        billing_start_date: formData.billing_start_date.toISOString().split('T')[0]
      };
      
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, data);
      } else {
        await axios.post(`${API}/clients`, data);
      }
      
      fetchClients();
      setShowForm(false);
      setEditingClient(null);
      resetForm();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };
  
  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      ...client,
      billing_start_date: new Date(client.billing_start_date)
    });
    setShowForm(true);
  };
  
  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  // WhatsApp image functionality (placeholder for Clients compatibility)
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageViaWhatsApp = async () => {
    alert('This feature is available in the Invoices section');
    setShowWhatsappImageModal(false);
    setImageFile(null);
    setImagePreview(null);
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      organization_name: '',
      gstin: '',
      pan: '',
      email: '',
      mobile: '',
      whatsapp: '',
      gstin_password: '',
      ewaybill_username: '',
      ewaybill_password: '',
      billing_start_date: new Date(),
      gst_enabled: false,
      state: ''
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Client Management
              </h1>
              <p className="text-gray-600 text-lg">Manage your clients with GST compliance and professional profiles</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Total Clients</div>
                <div className="text-2xl font-bold">{clients.length}</div>
              </div>
              <Button 
                onClick={() => navigate('/bulk-upload')}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk Upload
              </Button>
              <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-client-btn" 
                    onClick={() => { resetForm(); setEditingClient(null); }}
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Client
                  </Button>
                </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* GST Section First */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Switch
                    id="gst_enabled"
                    data-testid="gst-enabled-switch"
                    checked={formData.gst_enabled}
                    onCheckedChange={(checked) => setFormData({...formData, gst_enabled: checked})}
                  />
                  <Label htmlFor="gst_enabled" className="font-semibold text-blue-900">GST Enabled</Label>
                  <span className="text-xs text-blue-600 ml-auto">Enable to auto-fetch details from GST Portal</span>
                </div>
                
                {formData.gst_enabled && (
                <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gstin">GSTIN</Label>
                      <div className="flex gap-2">
                        <Input
                          id="gstin"
                          data-testid="gstin-input"
                          value={formData.gstin}
                          onChange={(e) => setFormData({...formData, gstin: e.target.value})}
                          placeholder="Enter 15-digit GSTIN"
                          maxLength="15"
                        />
                        <Button 
                          type="button"
                          onClick={handleAutoSyncGSTIN}
                          disabled={autoSyncing || !formData.gstin || formData.gstin.length !== 15}
                          className="bg-green-500 hover:bg-green-600 text-white whitespace-nowrap"
                        >
                          {autoSyncing ? (
                            <>
                              <span className="animate-spin mr-2">⏳</span>
                              Syncing...
                            </>
                          ) : (
                            <>
                              🔄 Auto Sync
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Enter GSTIN and click Auto Sync to fetch details from GST Portal</p>
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        data-testid="state-input"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        placeholder="e.g., Maharashtra, Gujarat"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gstin_username">GSTIN Username</Label>
                      <Input
                        id="gstin_username"
                        data-testid="gstin-username-input"
                        value={formData.gstin_username}
                        onChange={(e) => setFormData({...formData, gstin_username: e.target.value})}
                        placeholder="GST Portal username"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gstin_password">GSTIN Password</Label>
                      <Input
                        id="gstin_password"
                        type="password"
                        data-testid="gstin-password-input"
                        value={formData.gstin_password}
                        onChange={(e) => setFormData({...formData, gstin_password: e.target.value})}
                        placeholder="GST Portal password"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ewaybill_username">E-way Bill Username</Label>
                      <Input
                        id="ewaybill_username"
                        data-testid="ewaybill-username-input"
                        value={formData.ewaybill_username}
                        onChange={(e) => setFormData({...formData, ewaybill_username: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ewaybill_password">E-way Bill Password</Label>
                      <Input
                        id="ewaybill_password"
                        type="password"
                        data-testid="ewaybill-password-input"
                        value={formData.ewaybill_password}
                        onChange={(e) => setFormData({...formData, ewaybill_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}
              </div>
              
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Client Name * (Individual Contact)</Label>
                    <Input
                      id="name"
                      data-testid="client-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      placeholder="Will map to Legal Name if auto-synced"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization_name">Organization Name * (Business)</Label>
                    <Input
                      id="organization_name"
                      data-testid="organization-name-input"
                      value={formData.organization_name}
                      onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                      required
                      placeholder="Will map to Trade Name if auto-synced"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile *</Label>
                    <Input
                      id="mobile"
                      data-testid="mobile-input"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      data-testid="whatsapp-input"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pan">PAN</Label>
                    <Input
                      id="pan"
                      data-testid="pan-input"
                      value={formData.pan}
                      onChange={(e) => setFormData({...formData, pan: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Billing Start Date *</Label>
                <DatePicker
                  date={formData.billing_start_date}
                  onDateChange={(date) => setFormData({...formData, billing_start_date: date})}
                />
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-client-btn">
                  {editingClient ? 'Update' : 'Save'} Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Search and Filter Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Input
                    placeholder="Search clients by name, organization, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-3 text-lg border-orange-200 focus:border-orange-400 focus:ring-orange-400 rounded-xl"
                  />
                  <Users className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant={filterGST === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterGST('all')}
                  className={filterGST === 'all' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-200 text-orange-600 hover:bg-orange-50'}
                >
                  All ({clients.length})
                </Button>
                <Button
                  variant={filterGST === 'enabled' ? 'default' : 'outline'}
                  onClick={() => setFilterGST('enabled')}
                  className={filterGST === 'enabled' ? 'bg-green-500 hover:bg-green-600' : 'border-green-200 text-green-600 hover:bg-green-50'}
                >
                  GST Enabled ({clients.filter(c => c.gst_enabled).length})
                </Button>
                <Button
                  variant={filterGST === 'disabled' ? 'default' : 'outline'}
                  onClick={() => setFilterGST('disabled')}
                  className={filterGST === 'disabled' ? 'bg-gray-500 hover:bg-gray-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}
                >
                  GST Disabled ({clients.filter(c => !c.gst_enabled).length})
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  </div>
                  <div className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="h-10 bg-gray-200 rounded flex-1 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
            <Card key={client.id} className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">{client.name}</h3>
                    <p className="text-gray-600 font-medium">{client.organization_name}</p>
                  </div>
                  <Badge 
                    variant={client.gst_enabled ? "default" : "secondary"}
                    className={client.gst_enabled ? 
                      'bg-green-100 text-green-800 border-green-200' : 
                      'bg-gray-100 text-gray-600 border-gray-200'
                    }
                  >
                    {client.gst_enabled ? 'GST ✓' : 'No GST'}
                  </Badge>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-gray-600">
                    <MessageCircle className="w-4 h-4 mr-3 text-orange-500" />
                    <span className="text-sm">{client.email}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="w-4 h-4 mr-3 text-blue-500" />
                    <span className="text-sm">{client.mobile}</span>
                  </div>
                  {client.gst_enabled && client.gstin && (
                    <div className="flex items-center text-gray-600">
                      <FileText className="w-4 h-4 mr-3 text-green-500" />
                      <span className="text-sm font-mono">{client.gstin}</span>
                    </div>
                  )}
                  {client.state && (
                    <div className="flex items-center text-gray-600">
                      <TrendingUp className="w-4 h-4 mr-3 text-purple-500" />
                      <span className="text-sm">{client.state}</span>
                    </div>
                  )}
                </div>

                {/* GST Portal Information */}
                {(client.registration_type || client.constitution || client.gst_portal_data) && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      GST Portal Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {client.registration_type && (
                        <div>
                          <span className="text-blue-600 font-medium">Registration Type:</span>
                          <p className="text-gray-700">{client.registration_type}</p>
                        </div>
                      )}
                      {client.constitution && (
                        <div>
                          <span className="text-blue-600 font-medium">Constitution:</span>
                          <p className="text-gray-700">{client.constitution}</p>
                        </div>
                      )}
                      {client.registration_date && (
                        <div>
                          <span className="text-blue-600 font-medium">Registration Date:</span>
                          <p className="text-gray-700">{client.registration_date}</p>
                        </div>
                      )}
                      {client.gst_portal_data?.sts && (
                        <div>
                          <span className="text-blue-600 font-medium">Status:</span>
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                            client.gst_portal_data.sts === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {client.gst_portal_data.sts}
                          </span>
                        </div>
                      )}
                      {client.state_jurisdiction && (
                        <div>
                          <span className="text-blue-600 font-medium">State Jurisdiction:</span>
                          <p className="text-gray-700">{client.state_jurisdiction}</p>
                        </div>
                      )}
                      {client.central_jurisdiction && (
                        <div>
                          <span className="text-blue-600 font-medium">Central Jurisdiction:</span>
                          <p className="text-gray-700">{client.central_jurisdiction}</p>
                        </div>
                      )}
                      {client.einvoice_status !== null && client.einvoice_status !== undefined && (
                        <div>
                          <span className="text-blue-600 font-medium">E-Invoice:</span>
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                            client.einvoice_status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {client.einvoice_status ? 'Enabled' : 'Not Enabled'}
                          </span>
                        </div>
                      )}
                      {client.gst_portal_data?.nba && client.gst_portal_data.nba.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-blue-600 font-medium">Nature of Business:</span>
                          <p className="text-gray-700">{client.gst_portal_data.nba.join(', ')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex space-x-2 mt-4">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleEdit(client)}
                    className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(client.id)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredClients.length === 0 && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <CardContent className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No clients found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first client'}
              </p>
              <Button 
                onClick={() => { resetForm(); setEditingClient(null); setShowForm(true); }}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-8 py-3 rounded-xl"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Client
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{clients.filter(c => c.gst_enabled).length}</div>
              <div className="text-blue-100">GST Registered Clients</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{clients.length}</div>
              <div className="text-green-100">Total Active Clients</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{new Set(clients.map(c => c.state).filter(Boolean)).size}</div>
              <div className="text-purple-100">States Covered</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Image Upload Modal */}
      {showWhatsappImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Image via WhatsApp</h3>
                <button
                  onClick={() => {
                    setShowWhatsappImageModal(false);
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>To:</strong> {invoice.client_name}</p>
                  <p><strong>Mobile:</strong> {invoice.client_mobile}</p>
                  <p><strong>Invoice:</strong> {invoice.invoice_number}</p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={imagePreview} 
                        alt="Selected" 
                        className="max-w-full max-h-48 mx-auto rounded"
                      />
                      <div className="text-sm text-gray-600">
                        {imageFile?.name} ({(imageFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                      <button
                        onClick={() => document.getElementById('whatsapp-image-input').click()}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Choose different image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-gray-600 mb-2">Select an image to send with the invoice</p>
                        <button
                          onClick={() => document.getElementById('whatsapp-image-input').click()}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Select Image
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Max size: 5MB. Supported: JPG, PNG, GIF</p>
                    </div>
                  )}
                  
                  <input
                    id="whatsapp-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowWhatsappImageModal(false);
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={isSendingImage}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendImageViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!imageFile || isSendingImage}
                  >
                    {isSendingImage ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
 
    </div>
  );
};

// Modernized Table Display (Alternative view)
const ClientsTable = ({ clients, handleEdit, handleDelete }) => (
  <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-orange-100">
              <TableHead className="font-semibold text-gray-700">Name</TableHead>
              <TableHead className="font-semibold text-gray-700">Organization</TableHead>
              <TableHead className="font-semibold text-gray-700">Email</TableHead>
              <TableHead className="font-semibold text-gray-700">Mobile</TableHead>
              <TableHead className="font-semibold text-gray-700">GST Status</TableHead>
              <TableHead className="font-semibold text-gray-700">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.name}</TableCell>
                <TableCell>{client.organization_name}</TableCell>
                <TableCell>{client.email}</TableCell>
                <TableCell>{client.mobile}</TableCell>
                <TableCell>
                  <Badge variant={client.gst_enabled ? "default" : "secondary"}>
                    {client.gst_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(client)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(client.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

// Services, ClientServices, Invoices, Receipts, Expenses, and Documents components moved to /app/frontend/src/pages/

// Content Management System Component - Super Admin Only
const ContentManagement = () => {
  const [content, setContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingContent, setEditingContent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contentForm, setContentForm] = useState({
    key: '',
    value: '',
    section: 'landing'
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not super admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching CMS content from:', `${API}/cms/content`);
      console.log('Using token:', token ? 'Token present' : 'No token');
      console.log('User role:', user?.role);
      
      const response = await axios.get(`${API}/cms/content`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('CMS content response:', response.data);
      console.log('Number of items:', response.data?.length || 0);
      setContent(response.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 403) {
        alert('Access denied: Super Admin role required');
      } else {
        alert(`Error fetching content: ${error.response?.data?.detail || error.message}`);
      }
      setContent([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'super_admin') {
      fetchContent();
    }
  }, [user]);

  const handleEditContent = (item) => {
    setEditingContent(item);
    setContentForm({
      key: item.key,
      value: item.value,
      section: item.section
    });
    setShowEditModal(true);
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/cms/content`, contentForm);
      
      setShowEditModal(false);
      setEditingContent(null);
      setContentForm({ key: '', value: '', section: 'landing' });
      fetchContent();
      alert('Content updated successfully!');
      
    } catch (error) {
      console.error('Error updating content:', error);
      alert('Error updating content');
    }
  };

  const resetForm = () => {
    setContentForm({ key: '', value: '', section: 'landing' });
    setEditingContent(null);
    setShowEditModal(false);
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">Super Admin access required</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <CardTitle className="text-3xl font-bold text-gray-800 flex items-center justify-center">
            <Settings className="w-8 h-8 mr-3 text-purple-600" />
            Content Management System
          </CardTitle>
          <CardDescription className="text-gray-600 text-lg">
            Manage landing page content and website messaging
          </CardDescription>
        </div>

        {/* Action Buttons */}
        <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  Website Content
                </h1>
                <p className="text-gray-600 text-lg">Dynamically update landing page text and messaging</p>
              </div>
              
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                  <div className="text-sm font-medium">Content Items</div>
                  <div className="text-2xl font-bold">{content.length}</div>
                </div>
                <Button 
                  onClick={() => {
                    setEditingContent(null);
                    setShowEditModal(true);
                  }}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Content
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Table */}
        <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-bold flex items-center">
              <FileText className="w-6 h-6 mr-3" />
              Content Management
            </CardTitle>
            <CardDescription>
              Manage all website content and landing page text
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-purple-100">
                    <TableHead className="font-semibold text-gray-700">Section</TableHead>
                    <TableHead className="font-semibold text-gray-700">Key</TableHead>
                    <TableHead className="font-semibold text-gray-700">Value</TableHead>
                    <TableHead className="font-semibold text-gray-700">Last Updated</TableHead>
                    <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                
                <TableBody>
                  {content.map((item) => (
                    <TableRow key={item.id || `${item.section}-${item.key}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-medium">
                          {item.section.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{item.key}</TableCell>
                      <TableCell className="max-w-md">
                        <div className="truncate" title={item.value}>
                          {item.value}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleEditContent(item)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {isLoading && (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-lg font-medium">Loading content...</p>
              </div>
            )}
            
            {!isLoading && content.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Settings className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No content items found</p>
                <p>Start by adding your first content item</p>
                <Button 
                  onClick={fetchContent}
                  className="mt-4"
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Content Modal */}
      {showEditModal && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                {editingContent ? (
                  <>
                    <Edit className="w-5 h-5 mr-2 text-purple-600" />
                    Edit Content
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2 text-purple-600" />
                    Add Content
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {editingContent ? 'Update content item' : 'Create a new content item'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpdateContent} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="section">Section *</Label>
                  <Select 
                    value={contentForm.section} 
                    onValueChange={(value) => setContentForm({...contentForm, section: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="landing">Landing Page</SelectItem>
                      <SelectItem value="dashboard">Dashboard</SelectItem>
                      <SelectItem value="footer">Footer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="key">Content Key *</Label>
                  <Input
                    id="key"
                    type="text"
                    value={contentForm.key}
                    onChange={(e) => setContentForm({...contentForm, key: e.target.value})}
                    placeholder="e.g., hero_title, hero_subtitle"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="value">Content Value *</Label>
                <textarea
                  id="value"
                  value={contentForm.value}
                  onChange={(e) => setContentForm({...contentForm, value: e.target.value})}
                  placeholder="Enter the content text..."
                  required
                  className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg resize-y"
                />
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-800 mb-2">Content Guidelines:</h4>
                <div className="text-sm text-purple-700 space-y-1">
                  <p>• Use descriptive keys like: hero_title, hero_subtitle, feature_1_title</p>
                  <p>• Keep content clear and concise for better user experience</p>
                  <p>• Changes will be reflected on the website immediately</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                >
                  {editingContent ? 'Update Content' : 'Add Content'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Integrations Management Component (Super Admin Only)
const IntegrationsManagement = () => {
  console.log('🔵 IntegrationsManagement component mounted!');
  console.log('🔵 Current URL:', window.location.pathname);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  console.log('🔵 User:', user);
  console.log('🔵 User role:', user?.role);
  
  const [activeTab, setActiveTab] = useState('gst_return_status');
  const [loading, setLoading] = useState(false);
  const [gstSettings, setGstSettings] = useState({
    enabled: false,
    credentials: { api_url: '', api_key: '', asp_id: '', password: '' },
    settings: {}
  });
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: false,
    credentials: { api_url: '', api_key: '', phone_number_id: '', business_account_id: '' },
    settings: {}
  });
  const [publicSearchSettings, setPublicSearchSettings] = useState({
    enabled: false,
    credentials: { api_url: '', asp_id: '', password: '' },
    settings: {}
  });

  // Redirect check in useEffect
  React.useEffect(() => {
    console.log('🔵 useEffect running, user:', user);
    if (user && user.role !== 'super_admin') {
      console.log('🔴 Not super admin, redirecting');
      navigate('/dashboard');
    } else if (user) {
      console.log('🟢 User is super admin');
    }
  }, [user, navigate]);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching integrations from:', `${API}/integrations`);
      console.log('Using token:', token ? 'Token present' : 'No token');
      console.log('User role:', user?.role);
      
      const gst = await axios.get(`${API}/integrations/gst_return_status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const whatsapp = await axios.get(`${API}/integrations/whatsapp_official`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const publicSearch = await axios.get(`${API}/integrations/public_search_api`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('GST settings response:', gst.data);
      console.log('WhatsApp settings response:', whatsapp.data);
      console.log('Public Search settings response:', publicSearch.data);
      
      // Ensure credentials object exists
      setGstSettings({
        ...gst.data,
        credentials: gst.data.credentials || { api_url: '', api_key: '', asp_id: '', password: '' },
        settings: gst.data.settings || {}
      });
      setWhatsappSettings({
        ...whatsapp.data,
        credentials: whatsapp.data.credentials || { api_url: '', api_key: '', phone_number_id: '', business_account_id: '' },
        settings: whatsapp.data.settings || {}
      });
      setPublicSearchSettings({
        ...publicSearch.data,
        credentials: publicSearch.data.credentials || { api_url: '', asp_id: '', password: '' },
        settings: publicSearch.data.settings || {}
      });
      
      console.log('✅ Public Search settings loaded:', publicSearch.data);
      console.log('✅ Public Search credentials:', publicSearch.data.credentials);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);
      alert('Failed to load integrations. Please try again.');
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);
  
  // Debug: Log publicSearchSettings whenever it changes
  useEffect(() => {
    console.log('🔍 publicSearchSettings state changed:', publicSearchSettings);
  }, [publicSearchSettings]);

  const handleSaveGst = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/integrations/gst_return_status`, gstSettings);
      alert('GST Return Status API settings saved successfully!');
    } catch (error) {
      alert('Error saving settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/integrations/whatsapp_official`, whatsappSettings);
      alert('WhatsApp Official API settings saved successfully!');
    } catch (error) {
      alert('Error saving settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePublicSearch = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/integrations/public_search_api`, publicSearchSettings);
      alert('Public Search API settings saved successfully!');
    } catch (error) {
      alert('Error saving settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  console.log('🔵 About to return JSX');
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Integrations Management</h1>
        <p className="text-gray-600 mt-2">Configure API integrations for all users</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('gst_return_status')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gst_return_status'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              GST Return Status API
            </button>
            <button
              onClick={() => setActiveTab('whatsapp_official')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'whatsapp_official'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              WhatsApp Official API
            </button>
            <button
              onClick={() => setActiveTab('public_search_api')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'public_search_api'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Public Search API
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'gst_return_status' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">GST Return Status API</h3>
                  <p className="text-sm text-gray-500">Configure GST return tracking service for all users</p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gstSettings.enabled}
                    onChange={(e) => setGstSettings({...gstSettings, enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm font-medium">Enable Integration</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>API URL</Label>
                  <Input
                    value={gstSettings.credentials?.api_url || ''}
                    onChange={(e) => setGstSettings({
                      ...gstSettings,
                      credentials: {...gstSettings.credentials, api_url: e.target.value}
                    })}
                    placeholder="https://api.example.com/gst"
                  />
                </div>
                <div>
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={gstSettings.credentials?.api_key || ''}
                    onChange={(e) => setGstSettings({
                      ...gstSettings,
                      credentials: {...gstSettings.credentials, api_key: e.target.value}
                    })}
                    placeholder="Enter API key"
                  />
                </div>
                <div>
                  <Label>ASP ID</Label>
                  <Input
                    value={gstSettings.credentials?.asp_id || ''}
                    onChange={(e) => setGstSettings({
                      ...gstSettings,
                      credentials: {...gstSettings.credentials, asp_id: e.target.value}
                    })}
                    placeholder="Enter ASP ID"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={gstSettings.credentials?.password || ''}
                    onChange={(e) => setGstSettings({
                      ...gstSettings,
                      credentials: {...gstSettings.credentials, password: e.target.value}
                    })}
                    placeholder="Enter password"
                  />
                </div>
              </div>

              <Button onClick={handleSaveGst} disabled={loading}>
                {loading ? 'Saving...' : 'Save GST API Settings'}
              </Button>
            </div>
          )}

          {activeTab === 'whatsapp_official' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">WhatsApp Official Business API</h3>
                  <p className="text-sm text-gray-500">Configure WhatsApp Cloud API for all users</p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={whatsappSettings.enabled}
                    onChange={(e) => setWhatsappSettings({...whatsappSettings, enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm font-medium">Enable Integration</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>API URL</Label>
                  <Input
                    value={whatsappSettings.credentials?.api_url || ''}
                    onChange={(e) => setWhatsappSettings({
                      ...whatsappSettings,
                      credentials: {...whatsappSettings.credentials, api_url: e.target.value}
                    })}
                    placeholder="https://graph.facebook.com/v18.0"
                  />
                </div>
                <div>
                  <Label>API Key / Access Token</Label>
                  <Input
                    type="password"
                    value={whatsappSettings.credentials?.api_key || ''}
                    onChange={(e) => setWhatsappSettings({
                      ...whatsappSettings,
                      credentials: {...whatsappSettings.credentials, api_key: e.target.value}
                    })}
                    placeholder="Enter access token"
                  />
                </div>
                <div>
                  <Label>Phone Number ID</Label>
                  <Input
                    value={whatsappSettings.credentials?.phone_number_id || ''}
                    onChange={(e) => setWhatsappSettings({
                      ...whatsappSettings,
                      credentials: {...whatsappSettings.credentials, phone_number_id: e.target.value}
                    })}
                    placeholder="Enter phone number ID"
                  />
                </div>
                <div>
                  <Label>Business Account ID</Label>
                  <Input
                    value={whatsappSettings.credentials?.business_account_id || ''}
                    onChange={(e) => setWhatsappSettings({
                      ...whatsappSettings,
                      credentials: {...whatsappSettings.credentials, business_account_id: e.target.value}
                    })}
                    placeholder="Enter business account ID"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">📘 Setup Instructions</h4>
                <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                  <li>Create a Meta Business Account at business.facebook.com</li>
                  <li>Add WhatsApp product to your app</li>
                  <li>Get your Phone Number ID from WhatsApp Dashboard</li>
                  <li>Generate a permanent access token</li>
                  <li>Configure webhook for message delivery status</li>
                </ol>
              </div>

              <Button onClick={handleSaveWhatsApp} disabled={loading}>
                {loading ? 'Saving...' : 'Save WhatsApp API Settings'}
              </Button>
            </div>
          )}

          {activeTab === 'public_search_api' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Public Search GST API</h3>
                  <p className="text-sm text-gray-500">Configure GSTIN details auto-sync for client creation</p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={publicSearchSettings.enabled}
                    onChange={(e) => setPublicSearchSettings({...publicSearchSettings, enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm font-medium">Enable Integration</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>API URL</Label>
                  <Input
                    value={publicSearchSettings.credentials?.api_url || ''}
                    onChange={(e) => setPublicSearchSettings({
                      ...publicSearchSettings,
                      credentials: {...publicSearchSettings.credentials, api_url: e.target.value}
                    })}
                    placeholder="https://deprecatedgstapi.charteredinfo.com/commonapi/v1.1/search"
                  />
                </div>
                <div>
                  <Label>ASP ID</Label>
                  <Input
                    value={publicSearchSettings.credentials?.asp_id || ''}
                    onChange={(e) => setPublicSearchSettings({
                      ...publicSearchSettings,
                      credentials: {...publicSearchSettings.credentials, asp_id: e.target.value}
                    })}
                    placeholder="Enter ASP ID"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={publicSearchSettings.credentials?.password || ''}
                    onChange={(e) => setPublicSearchSettings({
                      ...publicSearchSettings,
                      credentials: {...publicSearchSettings.credentials, password: e.target.value}
                    })}
                    placeholder="Enter Password"
                  />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">✨ Features</h4>
                <ul className="text-sm text-green-800 space-y-1 ml-4 list-disc">
                  <li>Auto-fill client information from GST portal</li>
                  <li>Fetch legal name, trade name, and business type</li>
                  <li>Get complete address and registration details</li>
                  <li>Verify GSTIN status (Active/Cancelled)</li>
                  <li>Store complete GST portal data for future use</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">📘 How to Use</h4>
                <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                  <li>Configure API credentials here (already set by default)</li>
                  <li>Go to "Add Client" form</li>
                  <li>Enter a valid 15-digit GSTIN</li>
                  <li>Click "Auto Sync" button</li>
                  <li>Form fields will be automatically filled from GST portal</li>
                </ol>
              </div>

              <Button onClick={handleSavePublicSearch} disabled={loading}>
                {loading ? 'Saving...' : 'Save Public Search API Settings'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reset User Component - Super Admin Only
const ResetUser = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [dataCounts, setDataCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetOptions, setResetOptions] = useState({
    reset_clients: false,
    reset_services: false,
    reset_client_services: false,
    reset_invoices: false,
    reset_receipts: false,
    reset_expenses: false,
    reset_documents: false
  });

  // Redirect if not super admin
  React.useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/reset/users`);
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        alert('Failed to fetch users');
      }
    };
    if (user?.role === 'super_admin') {
      fetchUsers();
    }
  }, [user]);

  // Fetch data counts when user selected
  useEffect(() => {
    const fetchDataCounts = async () => {
      if (!selectedUser) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/admin/reset/user-data-count/${selectedUser}`);
        setDataCounts(response.data.counts);
      } catch (error) {
        console.error('Error fetching data counts:', error);
        alert('Failed to fetch data counts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDataCounts();
  }, [selectedUser]);

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    const user = users.find(u => u.id === userId);
    setSelectedUserName(user ? user.name : '');
    setResetResult(null);
    setResetOptions({
      reset_clients: false,
      reset_services: false,
      reset_client_services: false,
      reset_invoices: false,
      reset_receipts: false,
      reset_expenses: false,
      reset_documents: false
    });
  };

  const handleCheckboxChange = (e) => {
    setResetOptions({
      ...resetOptions,
      [e.target.name]: e.target.checked
    });
  };

  const handleExport = async () => {
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset/export-user-data?user_id=${selectedUser}`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedUserName}_backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleResetClick = () => {
    const hasSelection = Object.values(resetOptions).some(val => val === true);
    if (!hasSelection) {
      alert('Please select at least one data type to reset');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleResetConfirm = async () => {
    setShowConfirmModal(false);
    setResetting(true);
    
    try {
      const response = await axios.post(`${API}/admin/reset/reset-user-data`, {
        user_id: selectedUser,
        ...resetOptions
      });
      
      setResetResult(response.data.deleted_counts);
      
      // Refresh data counts
      const countsResponse = await axios.get(`${API}/admin/reset/user-data-count/${selectedUser}`);
      setDataCounts(countsResponse.data.counts);
      
      alert('User data reset successfully!');
    } catch (error) {
      console.error('Error resetting data:', error);
      alert('Failed to reset user data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResetting(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <RefreshCw className="w-8 h-8 mr-3 text-red-600" />
          Reset User Data
        </h1>
        <p className="text-gray-600 mt-2">Selectively reset user data with backup export option</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="mb-6">
          <Label>Select User</Label>
          <select
            value={selectedUser}
            onChange={handleUserSelect}
            className="w-full p-2 border rounded"
          >
            <option value="">-- Select a user --</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email}) - {user.role}
              </option>
            ))}
          </select>
        </div>

        {selectedUser && (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Current Data Count</h3>
              {loading ? (
                <p>Loading data counts...</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Clients</p>
                    <p className="text-2xl font-bold">{dataCounts.clients || 0}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Services</p>
                    <p className="text-2xl font-bold">{dataCounts.services || 0}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Client Services</p>
                    <p className="text-2xl font-bold">{dataCounts.client_services || 0}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Invoices</p>
                    <p className="text-2xl font-bold">{dataCounts.invoices || 0}</p>
                  </div>
                  <div className="bg-pink-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Receipts</p>
                    <p className="text-2xl font-bold">{dataCounts.receipts || 0}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Expenses</p>
                    <p className="text-2xl font-bold">{dataCounts.expenses || 0}</p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Documents</p>
                    <p className="text-2xl font-bold">{dataCounts.documents || 0}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Select Data to Reset</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_clients"
                    checked={resetOptions.reset_clients}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Clients ({dataCounts.clients || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_services"
                    checked={resetOptions.reset_services}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Services ({dataCounts.services || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_client_services"
                    checked={resetOptions.reset_client_services}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Client Services ({dataCounts.client_services || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_invoices"
                    checked={resetOptions.reset_invoices}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Invoices ({dataCounts.invoices || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_receipts"
                    checked={resetOptions.reset_receipts}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Receipts ({dataCounts.receipts || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_expenses"
                    checked={resetOptions.reset_expenses}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Expenses ({dataCounts.expenses || 0} records)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reset_documents"
                    checked={resetOptions.reset_documents}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mr-3"
                  />
                  <span>Documents ({dataCounts.documents || 0} records)</span>
                </label>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Download Backup Excel'}
              </Button>
              
              <Button
                onClick={handleResetClick}
                disabled={resetting}
                className="bg-red-600 hover:bg-red-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {resetting ? 'Resetting...' : 'Reset Selected Data'}
              </Button>
            </div>

            {resetResult && (
              <div className="mt-6 bg-green-50 border border-green-200 rounded p-4">
                <h4 className="font-semibold text-green-900 mb-2">Reset Complete!</h4>
                <p className="text-sm text-green-800">Deleted records:</p>
                <ul className="text-sm text-green-800 ml-4 mt-2">
                  {Object.entries(resetResult).map(([key, value]) => (
                    <li key={key}>{key}: {value} records</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-xl font-bold text-red-600 mb-4">⚠️ Confirm Reset</h3>
            <p className="mb-4">
              Are you sure you want to reset the selected data for <strong>{selectedUserName}</strong>?
            </p>
            <p className="mb-4 text-sm text-gray-600">
              This action cannot be undone. Make sure you have downloaded the backup Excel file first.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowConfirmModal(false)}
                className="bg-gray-500 hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, Reset Data
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Backup & Restore Component - Super Admin Only
const BackupRestore = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Redirect if not super admin
  React.useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/reset/users`);
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        alert('Failed to fetch users');
      }
    };
    if (user?.role === 'super_admin') {
      fetchUsers();
    }
  }, [user]);

  // Fetch backups when user selected
  useEffect(() => {
    if (selectedUser) {
      fetchBackups();
    } else {
      setBackups([]);
    }
  }, [selectedUser]);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup/list/${selectedUser}`);
      setBackups(response.data.backups);
    } catch (error) {
      console.error('Error fetching backups:', error);
      alert('Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    const user = users.find(u => u.id === userId);
    setSelectedUserName(user ? user.name : '');
  };

  const handleCreateBackup = async () => {
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/backup/create?user_id=${selectedUser}`);
      alert(`Backup created successfully!\nFilename: ${response.data.filename}`);
      fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Failed to create backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (backup) => {
    if (backup.download_url) {
      window.open(backup.download_url, '_blank');
    } else {
      alert('Download URL not available');
    }
  };

  const handleRestore = async (backup) => {
    const confirmed = window.confirm(
      `⚠️ WARNING: Restore backup for ${selectedUserName}?\n\n` +
      `This will restore:\n` +
      `${Object.entries(backup.collections_count || {}).map(([k, v]) => `- ${k}: ${v} records`).join('\n')}\n\n` +
      `Do you want to CLEAR EXISTING DATA before restore?\n\n` +
      `Click OK to CLEAR and restore\n` +
      `Click Cancel to MERGE with existing data`
    );
    
    if (confirmed === null) return; // User cancelled
    
    const clearData = confirmed; // OK = true (clear), Cancel = false (merge)
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/backup/restore`, {
        backup_id: backup.id,
        clear_existing_data: clearData
      });
      
      const restored = response.data.restored_counts;
      const restoredSummary = Object.entries(restored).map(([k, v]) => `${k}: ${v}`).join('\n');
      
      alert(`✅ Backup restored successfully!\n\nRestored:\n${restoredSummary}`);
      
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('Failed to restore backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (user?.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Database className="w-8 h-8 mr-3 text-blue-600" />
          Backup & Restore
        </h1>
        <p className="text-gray-600 mt-2">Create and manage user database backups</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="mb-6">
          <Label>Select User</Label>
          <select
            value={selectedUser}
            onChange={handleUserSelect}
            className="w-full p-2 border rounded"
          >
            <option value="">-- Select a user --</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email}) - {user.role}
              </option>
            ))}
          </select>
        </div>

        {selectedUser && (
          <>
            <div className="mb-6">
              <Button
                onClick={handleCreateBackup}
                disabled={creating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Database className="w-4 h-4 mr-2" />
                {creating ? 'Creating Backup...' : 'Create New Backup'}
              </Button>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Backup History</h3>
              
              {loading ? (
                <p>Loading backups...</p>
              ) : backups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No backups found for this user</p>
                  <p className="text-sm mt-2">Create your first backup using the button above</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Filename</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Size</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Collections</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.map(backup => (
                        <tr key={backup.id} className="border-t">
                          <td className="px-4 py-3 text-sm">{backup.filename}</td>
                          <td className="px-4 py-3 text-sm">{formatDate(backup.created_at)}</td>
                          <td className="px-4 py-3 text-sm">{formatFileSize(backup.file_size)}</td>
                          <td className="px-4 py-3 text-sm">
                            {backup.collections_count ? (
                              <div className="text-xs">
                                {Object.entries(backup.collections_count).map(([key, value]) => (
                                  <div key={key}>{key}: {value}</div>
                                ))}
                              </div>
                            ) : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleDownload(backup)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                              <Button
                                onClick={() => handleRestore(backup)}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                disabled={loading}
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">📝 Note</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Backups are stored securely in AWS S3</li>
                <li>• Download links are valid for 7 days</li>
                <li>• Backups include: Clients, Services, Invoices, Receipts, Expenses, Documents</li>
                <li>• <strong>Restore Options:</strong> Clear existing data (replace) OR Merge with existing data</li>
                <li>• ⚠️ Use restore carefully - test with a non-production user first</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Updates / Release Notes Component
const Updates = () => {
  console.log('🚀 Updates component mounted');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    features: [''],
    improvements: [''],
    bug_fixes: [''],
    breaking_changes: [''],
    is_published: true
  });

  useEffect(() => {
    console.log('🎯 Updates useEffect triggered');
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      console.log('📡 Fetching updates from:', `${API}/updates`);
      const response = await axios.get(`${API}/updates`);
      console.log('✅ Updates fetched successfully:', response.data);
      setUpdates(response.data);
    } catch (error) {
      console.error('❌ Error fetching updates:', error);
      console.error('Error details:', error.response);
      alert('Failed to fetch updates: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleListChange = (field, index, value) => {
    setFormData(prev => {
      const newList = [...prev[field]];
      newList[index] = value;
      return { ...prev, [field]: newList };
    });
  };

  const addListItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeListItem = (field, index) => {
    setFormData(prev => {
      const newList = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: newList.length === 0 ? [''] : newList };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty strings from arrays
    const cleanedData = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== ''),
      improvements: formData.improvements.filter(i => i.trim() !== ''),
      bug_fixes: formData.bug_fixes.filter(b => b.trim() !== ''),
      breaking_changes: formData.breaking_changes.filter(bc => bc.trim() !== '')
    };

    try {
      if (editingUpdate) {
        await axios.put(`${API}/admin/updates/${editingUpdate.id}`, cleanedData);
        alert('Update modified successfully!');
      } else {
        await axios.post(`${API}/admin/updates`, cleanedData);
        alert('Update created successfully!');
      }
      
      setShowCreateModal(false);
      setEditingUpdate(null);
      resetForm();
      fetchUpdates();
    } catch (error) {
      console.error('Error saving update:', error);
      alert(error.response?.data?.detail || 'Failed to save update');
    }
  };

  const handleEdit = (update) => {
    setEditingUpdate(update);
    setFormData({
      version: update.version,
      title: update.title,
      description: update.description,
      features: update.features.length > 0 ? update.features : [''],
      improvements: update.improvements.length > 0 ? update.improvements : [''],
      bug_fixes: update.bug_fixes.length > 0 ? update.bug_fixes : [''],
      breaking_changes: update.breaking_changes.length > 0 ? update.breaking_changes : [''],
      is_published: update.is_published
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/updates/${updateId}`);
      alert('Update deleted successfully!');
      fetchUpdates();
    } catch (error) {
      console.error('Error deleting update:', error);
      alert('Failed to delete update');
    }
  };

  const resetForm = () => {
    setFormData({
      version: '',
      title: '',
      description: '',
      features: [''],
      improvements: [''],
      bug_fixes: [''],
      breaking_changes: [''],
      is_published: true
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">✨ Application Updates</h1>
          <p className="text-gray-600 mt-2">Track all feature releases, improvements, and bug fixes</p>
        </div>
        {user?.role === 'super_admin' && (
          <Button onClick={() => {
            resetForm();
            setEditingUpdate(null);
            setShowCreateModal(true);
          }} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Update
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : updates.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Updates Yet</h3>
            <p className="text-gray-500">Updates and release notes will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {updates.map((update, index) => (
            <Card key={update.id} className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        v{update.version}
                      </Badge>
                      <span className="text-sm text-gray-500">{formatDate(update.release_date)}</span>
                    </div>
                    <CardTitle className="text-2xl">{update.title}</CardTitle>
                    <CardDescription className="mt-2 text-base">{update.description}</CardDescription>
                  </div>
                  {user?.role === 'super_admin' && (
                    <div className="flex gap-2">
                      <Button onClick={() => handleEdit(update)} variant="outline" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDelete(update.id)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {update.features.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-emerald-700 mb-2 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        New Features
                      </h4>
                      <ul className="space-y-1 text-gray-700">
                        {update.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-emerald-500 mr-2">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {update.improvements.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Improvements
                      </h4>
                      <ul className="space-y-1 text-gray-700">
                        {update.improvements.map((improvement, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            <span>{improvement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {update.bug_fixes.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-orange-700 mb-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Bug Fixes
                      </h4>
                      <ul className="space-y-1 text-gray-700">
                        {update.bug_fixes.map((fix, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-orange-500 mr-2">•</span>
                            <span>{fix}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {update.breaking_changes.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                        <XCircle className="w-4 h-4 mr-2" />
                        Breaking Changes
                      </h4>
                      <ul className="space-y-1 text-gray-700">
                        {update.breaking_changes.map((change, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-red-500 mr-2">•</span>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Update Modal */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingUpdate ? 'Edit Update' : 'Create New Update'}</DialogTitle>
              <DialogDescription>
                {editingUpdate ? 'Modify the release note details' : 'Add a new release note for the application'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="version">Version *</Label>
                  <Input
                    id="version"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    placeholder="e.g., 1.0.0"
                    required
                  />
                </div>
                <div className="flex items-center space-x-2 pt-8">
                  <Switch
                    id="is_published"
                    name="is_published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                  />
                  <Label htmlFor="is_published">Published</Label>
                </div>
              </div>

              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Major Update - Document Intelligence"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  placeholder="Detailed description of this release..."
                  required
                />
              </div>

              {/* Features */}
              <div>
                <Label>New Features</Label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={feature}
                      onChange={(e) => handleListChange('features', index, e.target.value)}
                      placeholder="Enter a new feature..."
                    />
                    {formData.features.length > 1 && (
                      <Button type="button" onClick={() => removeListItem('features', index)} variant="outline" size="sm">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" onClick={() => addListItem('features')} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Add Feature
                </Button>
              </div>

              {/* Improvements */}
              <div>
                <Label>Improvements</Label>
                {formData.improvements.map((improvement, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={improvement}
                      onChange={(e) => handleListChange('improvements', index, e.target.value)}
                      placeholder="Enter an improvement..."
                    />
                    {formData.improvements.length > 1 && (
                      <Button type="button" onClick={() => removeListItem('improvements', index)} variant="outline" size="sm">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" onClick={() => addListItem('improvements')} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Add Improvement
                </Button>
              </div>

              {/* Bug Fixes */}
              <div>
                <Label>Bug Fixes</Label>
                {formData.bug_fixes.map((fix, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={fix}
                      onChange={(e) => handleListChange('bug_fixes', index, e.target.value)}
                      placeholder="Enter a bug fix..."
                    />
                    {formData.bug_fixes.length > 1 && (
                      <Button type="button" onClick={() => removeListItem('bug_fixes', index)} variant="outline" size="sm">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" onClick={() => addListItem('bug_fixes')} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Add Bug Fix
                </Button>
              </div>

              {/* Breaking Changes */}
              <div>
                <Label>Breaking Changes</Label>
                {formData.breaking_changes.map((change, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={change}
                      onChange={(e) => handleListChange('breaking_changes', index, e.target.value)}
                      placeholder="Enter a breaking change..."
                    />
                    {formData.breaking_changes.length > 1 && (
                      <Button type="button" onClick={() => removeListItem('breaking_changes', index)} variant="outline" size="sm">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" onClick={() => addListItem('breaking_changes')} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Add Breaking Change
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" onClick={() => {
                  setShowCreateModal(false);
                  setEditingUpdate(null);
                  resetForm();
                }} variant="outline">
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  {editingUpdate ? 'Update' : 'Create'} Release Note
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Forgot Password Component
// Live Environment Variables Component
const LiveVariables = () => {
  const [variables, setVariables] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVariables();
  }, []);

  const fetchVariables = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/live-variables`);
      setVariables(response.data);
    } catch (error) {
      console.error('Error fetching variables:', error);
      alert('Failed to fetch environment variables');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="mb-8">
          <div className="h-10 bg-gray-200 rounded w-96 animate-pulse mb-3"></div>
          <div className="h-5 bg-gray-200 rounded w-64 animate-pulse"></div>
        </div>
        <div className="space-y-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="mb-6">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="flex items-center border-b pb-3">
                      <div className="w-1/3 h-5 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-2/3 h-8 bg-gray-200 rounded ml-4 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">🔧 Live Environment Variables</h1>
        <p className="text-gray-600 mt-2">Current environment configuration (read-only view)</p>
      </div>

      {variables && Object.entries(variables).map(([category, vars]) => (
        <Card key={category} className="mb-6">
          <CardHeader>
            <CardTitle className="capitalize text-xl">{category.replace('_', ' ')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(vars).map(([key, value]) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center border-b pb-3 last:border-0">
                  <div className="w-full sm:w-1/3 font-medium text-gray-700 mb-1 sm:mb-0">{key}</div>
                  <div className="w-full sm:w-2/3">
                    <code className={`px-3 py-1 rounded text-sm ${
                      value === '***HIDDEN***' 
                        ? 'bg-red-100 text-red-700' 
                        : value === 'Not set' 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {value}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


// ForgotPassword, ResetPassword, and EmailVerification components moved to /app/frontend/src/pages/



// Mobile Invoice Sharing Component
const MobileInvoiceShare = ({ invoice, onClose }) => {
  const shareViaWhatsApp = () => {
    const invoiceText = `🧾 *INVOICE*
━━━━━━━━━━━━━━━━━━━━
📋 #${invoice.invoice_number}
📅 Due: ${new Date(invoice.due_date).toLocaleDateString()}
👤 ${invoice.client?.name || 'Client'}
💰 ₹${invoice.total_amount}
━━━━━━━━━━━━━━━━━━━━
_Via GSTAssist™_`;

    const url = `https://wa.me/?text=${encodeURIComponent(invoiceText)}`;
    window.open(url, '_blank');
    onClose();
  };

  const shareViaTelegram = () => {
    const invoiceText = `Invoice #${invoice.invoice_number} - ₹${invoice.total_amount}`;
    const url = `https://t.me/share/url?text=${encodeURIComponent(invoiceText)}`;
    window.open(url, '_blank');
    onClose();
  };

  const shareViaEmail = () => {
    const subject = `Invoice ${invoice.invoice_number}`;
    const body = `Please find invoice details:\n\nInvoice #: ${invoice.invoice_number}\nAmount: ₹${invoice.total_amount}\nDue Date: ${new Date(invoice.due_date).toLocaleDateString()}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    onClose();
  };

  const shareNative = async () => {
    const invoiceData = {
      title: `Invoice ${invoice.invoice_number}`,
      text: `Invoice #${invoice.invoice_number} - Amount: ₹${invoice.total_amount}`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(invoiceData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Native share error:', error);
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(invoiceData.text);
        alert('Invoice details copied to clipboard!');
      } catch (error) {
        console.error('Clipboard error:', error);
      }
    }
    onClose();
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Share Invoice #{invoice.invoice_number}</h3>
      
      <div className="grid grid-cols-2 gap-3">
        <Button 
          onClick={shareViaWhatsApp}
          className="bg-green-600 hover:bg-green-700 text-white p-4 h-auto flex-col"
        >
          <MessageCircle className="w-6 h-6 mb-2" />
          WhatsApp
        </Button>
        
        <Button 
          onClick={shareViaTelegram}
          className="bg-blue-500 hover:bg-blue-600 text-white p-4 h-auto flex-col"
        >
          <Send className="w-6 h-6 mb-2" />
          Telegram
        </Button>
        
        <Button 
          onClick={shareViaEmail}
          className="bg-gray-600 hover:bg-gray-700 text-white p-4 h-auto flex-col"
        >
          <Mail className="w-6 h-6 mb-2" />
          Email
        </Button>
        
        <Button 
          onClick={shareNative}
          className="bg-purple-600 hover:bg-purple-700 text-white p-4 h-auto flex-col"
        >
          <Share className="w-6 h-6 mb-2" />
          More Apps
        </Button>
      </div>
      
      <Button onClick={onClose} variant="outline" className="w-full">
        Cancel
      </Button>
    </div>
  );
};

// QR Code Modal Component
const QrCodeModal = ({ isOpen, onClose, qrCode, onRefresh, isConnected }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Set timeout for QR loading
  useEffect(() => {
    if (isOpen && !qrCode && !isConnected) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 seconds timeout

      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [isOpen, qrCode, isConnected]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError(null);
    setLoadingTimeout(false);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      console.log('QR refreshed successfully');
    } catch (error) {
      console.error('Error refreshing QR code:', error);
      setError('Failed to refresh QR code. WhatsApp service may be unavailable.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center flex items-center justify-center">
            <MessageCircle className="w-6 h-6 mr-2 text-green-600" />
            WhatsApp Web Connection
          </DialogTitle>
          <DialogDescription className="text-center">
            {isConnected ? 'Connection successful!' : 'Scan this QR code with your WhatsApp mobile app to connect'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-6 py-6">
          {isConnected ? (
            <div className="text-center space-y-4">
              <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <p className="text-green-800 font-bold text-lg">Connected Successfully!</p>
                <p className="text-green-600 text-sm mt-2">WhatsApp Web is now connected and ready to use</p>
              </div>
              <p className="text-xs text-gray-500">This dialog will close automatically...</p>
            </div>
          ) : qrCode ? (
            <>
              <div className="bg-white p-4 rounded-lg shadow-lg border-2 border-green-200">
                <QRCodeSVG 
                  value={qrCode} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              <div className="text-center space-y-3">
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <p className="text-green-800 font-semibold mb-2">📱 How to connect:</p>
                  <ol className="text-sm text-green-700 space-y-1 text-left">
                    <li>1. Open WhatsApp on your phone</li>
                    <li>2. Go to Settings → Linked Devices</li>
                    <li>3. Tap "Link a Device"</li>
                    <li>4. Scan this QR code</li>
                  </ol>
                </div>
                
                <p className="text-xs text-gray-500">
                  Keep this window open until connection is established
                </p>
                
                <Button 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  variant="outline"
                  size="sm"
                  className="text-green-600 border-green-200 hover:bg-green-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Refreshing...
                    </>
                  ) : (
                    'Refresh QR Code'
                  )}
                </Button>
              </div>
            </>
          ) : error ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-red-800 font-medium">Connection Error</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
              <Button 
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="bg-green-600 hover:bg-green-700"
              >
                {isRefreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Retrying...
                  </>
                ) : (
                  'Try Again'
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              {loadingTimeout ? (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <p className="text-yellow-800 font-medium">QR Generation Taking Longer</p>
                    <p className="text-sm text-yellow-600 mt-1">WhatsApp service might be initializing. Please try refreshing.</p>
                  </div>
                  <Button 
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isRefreshing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Refreshing...
                      </>
                    ) : (
                      'Refresh QR Code'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                  <p className="text-gray-600">Generating QR code...</p>
                  <p className="text-xs text-gray-500">This may take a few seconds</p>
                  <Button 
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    variant="outline"
                    size="sm"
                  >
                    Refresh Now
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex justify-center">
          <Button onClick={onClose} variant="outline" className="px-8">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Business API Configuration Form Component
const BusinessApiConfigForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    phone_number_id: '',
    access_token: '',
    webhook_verify_token: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      setFormData({ phone_number_id: '', access_token: '', webhook_verify_token: '' });
    } catch (error) {
      console.error('Configuration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <div>
          <Label htmlFor="phone_number_id">Phone Number ID *</Label>
          <Input
            id="phone_number_id"
            placeholder="Enter WhatsApp Phone Number ID"
            value={formData.phone_number_id}
            onChange={(e) => setFormData({...formData, phone_number_id: e.target.value})}
            required
          />
          <p className="text-xs text-gray-500 mt-1">Found in Meta Developers → WhatsApp → Configuration</p>
        </div>
        
        <div>
          <Label htmlFor="access_token">Access Token *</Label>
          <Input
            id="access_token"
            type="password"
            placeholder="Enter WhatsApp Access Token"
            value={formData.access_token}
            onChange={(e) => setFormData({...formData, access_token: e.target.value})}
            required
          />
          <p className="text-xs text-gray-500 mt-1">Use permanent token from Business Manager</p>
        </div>
        
        <div>
          <Label htmlFor="webhook_verify_token">Webhook Verify Token *</Label>
          <Input
            id="webhook_verify_token"
            placeholder="Enter Webhook Verify Token"
            value={formData.webhook_verify_token}
            onChange={(e) => setFormData({...formData, webhook_verify_token: e.target.value})}
            required
          />
          <p className="text-xs text-gray-500 mt-1">Custom token for webhook verification</p>
        </div>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
          <div className="text-sm">
            <p className="font-medium text-yellow-800">Important Notes:</p>
            <ul className="text-yellow-700 mt-1 space-y-1">
              <li>• These credentials will be stored securely</li>
              <li>• Only Super Admin can configure Business API</li>
              <li>• Regular users need admin approval</li>
              <li>• See setup instructions for detailed steps</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 pt-4">
        <DialogClose asChild>
          <Button type="button" variant="outline">Cancel</Button>
        </DialogClose>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? 'Configuring...' : 'Configure API'}
        </Button>
      </div>
    </form>

  );
};

// Create Invoice Component
const CreateInvoice = () => {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    service_id: '',
    custom_rate: '',
    quantity: 1,
    due_date: '',
    notes: '',
    invoice_type: 'adhoc'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchServices();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const data = {
        ...formData,
        custom_rate: parseFloat(formData.custom_rate) || null,
        quantity: parseInt(formData.quantity) || 1,
        invoice_date: new Date().toISOString().split('T')[0]
      };
      
      await axios.post(`${API}/invoices/create-adhoc`, data);
      alert('Invoice created successfully!');
      
      // Reset form
      setFormData({
        client_id: '',
        service_id: '',
        custom_rate: '',
        quantity: 1,
        due_date: '',
        notes: '',
        invoice_type: 'adhoc'
      });
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Error creating invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedService = services.find(s => s.id === formData.service_id);
  const rate = formData.custom_rate ? parseFloat(formData.custom_rate) : (selectedService?.default_rate || 0);
  const quantity = parseInt(formData.quantity) || 1;
  const subtotal = rate * quantity;
  const gstAmount = subtotal * ((selectedService?.gst_rate || 0) / 100);
  const totalAmount = subtotal + gstAmount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Create Invoice
              </h1>
              <p className="text-gray-600 text-lg">Generate adhoc invoices for special services and one-time charges</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Invoice Type</div>
                <div className="text-2xl font-bold">Adhoc</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto space-y-8">
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <Receipt className="w-6 h-6 mr-3" />
              Invoice Details
            </CardTitle>
            <CardDescription className="text-purple-100">
              Fill in the details to create a new adhoc invoice
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="client_id">Select Client *</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} - {client.organization_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="service_id">Select Service *</Label>
                  <Select value={formData.service_id} onValueChange={(value) => setFormData({...formData, service_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} - ₹{service.default_rate}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="custom_rate">Custom Rate (₹)</Label>
                  <Input
                    id="custom_rate"
                    type="number"
                    step="0.01"
                    placeholder={selectedService ? `Default: ₹${selectedService.default_rate}` : "Enter custom rate"}
                    value={formData.custom_rate}
                    onChange={(e) => setFormData({...formData, custom_rate: e.target.value})}
                  />
                  <p className="text-sm text-gray-600 mt-1">Leave blank to use service default rate</p>
                </div>

                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Invoice Notes</Label>
                <textarea
                  id="notes"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Add any additional notes or special instructions..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              {/* Invoice Summary */}
              {selectedService && (
                <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-purple-800">Invoice Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-gray-700">
                      <span>Rate per unit:</span>
                      <span>₹{rate.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Quantity:</span>
                      <span>{quantity}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal:</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>GST ({selectedService.gst_rate}%):</span>
                      <span>₹{gstAmount.toFixed(2)}</span>
                    </div>
                    <hr className="border-purple-200" />
                    <div className="flex justify-between text-lg font-bold text-purple-800">
                      <span>Total Amount:</span>
                      <span>₹{totalAmount.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end space-x-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setFormData({
                    client_id: '',
                    service_id: '',
                    custom_rate: '',
                    quantity: 1,
                    due_date: '',
                    notes: '',
                    invoice_type: 'adhoc'
                  })}
                >
                  Reset Form
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting || !formData.client_id || !formData.service_id || !formData.due_date}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  {isSubmitting ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Edit Invoice Component
const EditInvoice = ({ invoice, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    status: invoice.status || 'draft',
    invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '',
    due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
    client_id: invoice.client_id || '',
    notes: invoice.notes || '',
    amount: invoice.amount || 0,
    gst_rate: invoice.gst_rate || 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState([]);

  // Fetch clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${API}/clients`);
        setClients(response.data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    fetchClients();
  }, []);

  // Update due_date when invoice_date changes (RULE: due_date = invoice_date)
  useEffect(() => {
    if (formData.invoice_date) {
      setFormData(prev => ({ ...prev, due_date: prev.invoice_date }));
    }
  }, [formData.invoice_date]);

  const calculateTotals = () => {
    const amount = parseFloat(formData.amount) || 0;
    const gstRate = parseFloat(formData.gst_rate) || 0;
    const gstAmount = amount * (gstRate / 100);
    const totalAmount = amount + gstAmount;
    return { amount, gstAmount, totalAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { gstAmount, totalAmount } = calculateTotals();
      
      const updateData = {
        ...formData,
        amount: parseFloat(formData.amount),
        gst_rate: parseFloat(formData.gst_rate),
        gst_amount: gstAmount,
        total_amount: totalAmount,
        due_date: formData.invoice_date  // RULE: Due date = Invoice date
      };

      await axios.put(`${API}/invoices/${invoice.id}`, updateData);
      alert('Invoice updated successfully!');
      onSave();
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Error updating invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { gstAmount, totalAmount } = calculateTotals();

  return (
    <div className="p-6">
      <DialogHeader className="mb-6">
        <DialogTitle className="text-2xl font-bold text-gray-800 flex items-center">
          <Edit className="w-6 h-6 mr-3 text-blue-600" />
          Edit Invoice #{invoice.invoice_number}
        </DialogTitle>
        <DialogDescription className="text-gray-600">
          Update invoice details and recalculate totals
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="client_id">Client *</Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} - {client.company || 'No Company'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">Invoice Status *</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="invoice_date">Invoice Date *</Label>
            <Input
              id="invoice_date"
              type="date"
              value={formData.invoice_date}
              onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="due_date">Due Date (Auto-set = Invoice Date)</Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              disabled
              className="bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Due date automatically matches invoice date</p>
          </div>

          <div>
            <Label htmlFor="amount">Amount (₹) *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="gst_rate">GST Rate (%) *</Label>
            <Input
              id="gst_rate"
              type="number"
              step="0.01"
              value={formData.gst_rate}
              onChange={(e) => setFormData({...formData, gst_rate: e.target.value})}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder="Add any additional notes..."
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
          />
        </div>

        {/* Invoice Calculation Preview */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg text-blue-800">Updated Calculation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-gray-700">
              <span>Base Amount:</span>
              <span>₹{parseFloat(formData.amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>GST ({formData.gst_rate}%):</span>
              <span>₹{gstAmount.toFixed(2)}</span>
            </div>
            <hr className="border-blue-200" />
            <div className="flex justify-between text-lg font-bold text-blue-800">
              <span>Total Amount:</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {isSubmitting ? 'Updating...' : 'Update Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// View Invoice Component
const ViewInvoice = ({ invoice, onClose }) => {
  const { user } = useAuth();
  const [isSharing, setIsSharing] = useState(false);
  
  // WhatsApp Image Upload States
  const [showWhatsappImageModal, setShowWhatsappImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSendingImage, setIsSendingImage] = useState(false);

  // Helper function to calculate GST breakdown based on GSTIN first 2 digits
  const calculateGSTBreakdown = (invoice, user) => {
    const baseAmount = invoice.base_amount || invoice.amount || 0;
    const gstRate = invoice.gst_rate || 0;
    
    // Check if taxes should be applied
    if (user?.gstin && invoice.client_gst_enabled && gstRate > 0) {
      // Compare GSTIN first 2 digits instead of state names
      const userGstinPrefix = user?.gstin?.substring(0, 2) || '';
      const clientGstin = invoice.client_gstin || invoice.client?.gstin || '';
      const clientGstinPrefix = clientGstin?.substring(0, 2) || '';
      
      console.log('GST Calculation - User GSTIN prefix:', userGstinPrefix, 'Client GSTIN prefix:', clientGstinPrefix);
      
      if (userGstinPrefix && clientGstinPrefix && userGstinPrefix === clientGstinPrefix) {
        // Same state (same GSTIN prefix): CGST + SGST
        const cgstRate = gstRate / 2;
        const sgstRate = gstRate / 2;
        const cgstAmount = (baseAmount * cgstRate) / 100;
        const sgstAmount = (baseAmount * sgstRate) / 100;
        
        console.log('Intrastate GST - CGST:', cgstRate, '% SGST:', sgstRate, '%');
        
        return {
          type: 'intrastate',
          cgstRate,
          sgstRate,
          cgstAmount,
          sgstAmount,
          totalGst: cgstAmount + sgstAmount
        };
      } else {
        // Different state (different GSTIN prefix): IGST
        const igstAmount = (baseAmount * gstRate) / 100;
        
        console.log('Interstate GST - IGST:', gstRate, '%');
        
        return {
          type: 'interstate',
          igstRate: gstRate,
          igstAmount,
          totalGst: igstAmount
        };
      }
    }
    
    return {
      type: 'no_gst',
      totalGst: 0
    };
  };

  const printInvoice = () => {
    // Calculate GST breakdown
    const gstBreakdown = calculateGSTBreakdown(invoice, user);
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          @page {
            size: A4;
            margin: 20mm;
          }
          
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background: white;
          }
          
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
          }
          
          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 40px;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
          }
          
          .company-info {
            flex: 1;
          }
          
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 5px;
          }
          
          .company-details {
            color: #666;
            font-size: 14px;
          }
          
          .invoice-title {
            text-align: right;
            flex: 1;
          }
          
          .invoice-title h1 {
            font-size: 36px;
            color: #2563eb;
            margin-bottom: 5px;
          }
          
          .invoice-number {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .invoice-dates {
            font-size: 14px;
            color: #666;
          }
          
          .billing-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 40px;
          }
          
          .billing-info {
            flex: 1;
            padding-right: 40px;
          }
          
          .billing-info h3 {
            color: #2563eb;
            margin-bottom: 10px;
            font-size: 18px;
          }
          
          .billing-details {
            background: #f8fafc;
            padding: 20px;
            border-radius: 8px;
            border-left: 4px solid #2563eb;
          }
          
          .billing-details p {
            margin-bottom: 5px;
            font-size: 14px;
          }
          
          .services-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 40px;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .services-table th {
            background: #2563eb;
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
          }
          
          .services-table td {
            padding: 15px;
            border-bottom: 1px solid #e5e7eb;
          }
          
          .services-table tr:last-child td {
            border-bottom: none;
          }
          
          .amount-section {
            text-align: right;
            margin-bottom: 40px;
          }
          
          .amount-row {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 10px;
          }
          
          .amount-label {
            width: 200px;
            text-align: right;
            padding-right: 20px;
            font-weight: 500;
          }
          
          .amount-value {
            width: 120px;
            text-align: right;
            font-weight: 600;
          }
          
          .total-row {
            border-top: 2px solid #2563eb;
            padding-top: 10px;
            font-size: 18px;
            color: #2563eb;
          }
          
          .footer-section {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          
          .payment-terms {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          
          .payment-terms h4 {
            color: #92400e;
            margin-bottom: 10px;
          }
          
          @media print {
            body { 
              font-size: 12px; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .invoice-container { 
              padding: 20px; 
              box-shadow: none;
            }
            .no-print { 
              display: none !important; 
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <div class="company-info">
              <div class="company-name">${user?.organization_name || 'Organization'}</div>
              <div class="company-details">
                ${user?.address ? `<p>${user.address}</p>` : ''}
                ${user?.city || user?.state || user?.pincode ? `<p>${[user?.city, user?.state].filter(Boolean).join(', ')}${user?.pincode ? ` - ${user.pincode}` : ''}</p>` : ''}
                <p>${user?.email}</p>
                ${user?.mobile ? `<p>Mobile: ${user.mobile}</p>` : ''}
                ${user?.gstin ? `<p><strong>GSTIN:</strong> ${user.gstin}</p>` : ''}
              </div>
            </div>
            <div class="invoice-title">
              <h1>${user?.gstin ? 'TAX INVOICE' : 'INVOICE'}</h1>
              <div class="invoice-number">#${invoice.invoice_number}</div>
              <div class="invoice-dates">
                <div><strong>Date:</strong> ${new Date(invoice.invoice_date).toLocaleDateString('en-IN')}</div>
                <div><strong>Place of Supply:</strong> ${user?.state || 'Not Specified'}</div>
              </div>
            </div>
          </div>

          <div class="billing-section">
            <div class="billing-info">
              <h3>Bill To:</h3>
              <div class="billing-details">
                <p><strong>${invoice.client_name}</strong></p>
                ${invoice.organization_name ? `<p>${invoice.organization_name}</p>` : ''}
                ${invoice.client?.address ? `<p>${invoice.client.address}</p>` : ''}
                ${invoice.client?.city || invoice.client?.state || invoice.client?.pincode ? `<p>${[invoice.client?.city, invoice.client?.state].filter(Boolean).join(', ')}${invoice.client?.pincode ? ` - ${invoice.client.pincode}` : ''}</p>` : ''}
                ${invoice.client_email ? `<p>Email: ${invoice.client_email}</p>` : ''}
                ${invoice.client_mobile ? `<p>Mobile: ${invoice.client_mobile}</p>` : ''}
                ${invoice.client_gstin ? `<p><strong>GSTIN:</strong> ${invoice.client_gstin}</p>` : ''}
              </div>
            </div>
            ${invoice.status !== 'draft' ? `
            <div class="billing-info">
              <h3>Invoice Status:</h3>
              <div class="billing-details">
                <p><strong>Status:</strong> <span style="color: ${invoice.status === 'paid' ? '#16a34a' : invoice.status === 'sent' ? '#2563eb' : '#f59e0b'}">${invoice.status.toUpperCase()}</span></p>
                ${invoice.sent_at ? `<p><strong>Sent:</strong> ${new Date(invoice.sent_at).toLocaleDateString('en-IN')}</p>` : ''}
                ${invoice.paid_at ? `<p><strong>Paid:</strong> ${new Date(invoice.paid_at).toLocaleDateString('en-IN')}</p>` : ''}
              </div>
            </div>
            ` : ''}
          </div>

          <table class="services-table">
            <thead>
              <tr>
                <th>Service Description</th>
                <th style="width: 120px; text-align: right;">Amount</th>
                <th style="width: 100px; text-align: right;">GST Rate</th>
                <th style="width: 120px; text-align: right;">GST Amount</th>
                <th style="width: 120px; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div><strong>${invoice.service_name}</strong></div>
                  ${invoice.service_description ? `<div style="color: #666; font-size: 12px;">${invoice.service_description}</div>` : ''}
                </td>
                <td style="text-align: right;">₹${invoice.base_amount?.toLocaleString('en-IN')}</td>
                <td style="text-align: right;">${invoice.gst_rate || 0}%</td>
                <td style="text-align: right;">₹${invoice.gst_amount?.toLocaleString('en-IN')}</td>
                <td style="text-align: right;"><strong>₹${invoice.total_amount?.toLocaleString('en-IN')}</strong></td>
              </tr>
            </tbody>
          </table>

          <div class="amount-section">
            <div class="amount-row">
              <div class="amount-label">Subtotal:</div>
              <div class="amount-value">₹${(invoice.base_amount || invoice.amount || 0).toLocaleString('en-IN')}</div>
            </div>
            
            ${gstBreakdown.type === 'intrastate' ? `
            <div class="amount-row">
              <div class="amount-label">CGST (${gstBreakdown.cgstRate}%):</div>
              <div class="amount-value">₹${gstBreakdown.cgstAmount.toLocaleString('en-IN')}</div>
            </div>
            <div class="amount-row">
              <div class="amount-label">SGST (${gstBreakdown.sgstRate}%):</div>
              <div class="amount-value">₹${gstBreakdown.sgstAmount.toLocaleString('en-IN')}</div>
            </div>
            ` : ''}
            
            ${gstBreakdown.type === 'interstate' ? `
            <div class="amount-row">
              <div class="amount-label">IGST (${gstBreakdown.igstRate}%):</div>
              <div class="amount-value">₹${gstBreakdown.igstAmount.toLocaleString('en-IN')}</div>
            </div>
            ` : ''}
            
            ${gstBreakdown.type === 'no_gst' ? `
            <div class="amount-row">
              <div class="amount-label">Tax:</div>
              <div class="amount-value">₹0.00 (No GST)</div>
            </div>
            ` : ''}
            
            <div class="amount-row total-row">
              <div class="amount-label">Total Amount:</div>
              <div class="amount-value">₹${((invoice.base_amount || invoice.amount || 0) + gstBreakdown.totalGst).toLocaleString('en-IN')}</div>
            </div>
          </div>

          <div class="payment-terms">
            <h4>Payment Terms & Conditions:</h4>
            <p>Payment is due within 30 days from the invoice date. Late payment may incur additional charges. Please make payment to the bank details provided separately. For any queries, contact us at ${user.email}.</p>
          </div>

          <div class="footer-section">
            <p><strong>Thank you for your business!</strong></p>
            <p>This is a computer-generated invoice and does not require a physical signature.</p>
            <p style="margin-top: 10px; font-size: 12px;">Generated on ${new Date().toLocaleDateString('en-IN')} at ${new Date().toLocaleTimeString('en-IN')}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1000);
  };

  // Generate shareable invoice content (without GstAssist branding)
  const generateInvoiceText = () => {
    return `🧾 *INVOICE*
━━━━━━━━━━━━━━━━━━━━

📋 *Invoice #:* ${invoice.invoice_number}
📅 *Date:* ${new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
📅 *Due Date:* ${new Date(invoice.due_date).toLocaleDateString('en-IN')}

🏢 *From:*
*${user.organization_name}*
${user.email}
${user.mobile ? user.mobile : ''}
${user.gstin ? `GSTIN: ${user.gstin}` : ''}

👤 *Bill To:*
*${invoice.client_name}*
${invoice.organization_name || ''}
${invoice.client_email || ''}
${invoice.client_mobile || ''}
${invoice.client_gstin ? `GSTIN: ${invoice.client_gstin}` : ''}

📝 *Service Details:*
*${invoice.service_name}*
${invoice.service_description || ''}

💰 *Amount Breakdown:*
• Base Amount: ₹${invoice.base_amount?.toLocaleString('en-IN')}
• GST (${invoice.gst_rate || 0}%): ₹${invoice.gst_amount?.toLocaleString('en-IN')}
• *Total Amount: ₹${invoice.total_amount?.toLocaleString('en-IN')}*

📄 *Status:* ${invoice.status?.toUpperCase()}

💳 *Payment Terms:*
Payment due within 30 days from invoice date.
For queries, contact: ${user.email}

━━━━━━━━━━━━━━━━━━━━
*${user.organization_name}*
_Professional ${user.profession || 'Services'}_

Thank you for your business! 🙏`;
  };

  // Send invoice via WhatsApp
  const sendViaWhatsApp = async () => {
    try {
      setIsSharing(true);
      const invoiceText = generateInvoiceText();
      
      // For mobile devices, use WhatsApp URL scheme
      if (window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(invoiceText)}`;
        window.open(whatsappUrl, '_blank');
      } else {
        // For desktop, use our WhatsApp Web integration
        const response = await axios.post(`${API}/whatsapp/web/send`, {
          phone_number: invoice.client_mobile,
          message: invoiceText
        });
        
        if (response.data.success) {
          alert('Invoice sent via WhatsApp successfully!');
        } else {
          throw new Error('Failed to send message');
        }
      }
    } catch (error) {
      console.error('Error sending via WhatsApp:', error);
      alert('Error sending invoice via WhatsApp');
    } finally {
      setIsSharing(false);
    }
  };

  // WhatsApp image functionality 
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageViaWhatsApp = async () => {
    if (!imageFile) {
      alert('Please select an image first');
      return;
    }
    
    if (!invoice.client_mobile) {
      alert('Client mobile number not found');
      return;
    }
    
    try {
      setIsSendingImage(true);
      
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
          
          // Prepare message data
          const messageData = {
            phone_number: invoice.client_mobile,
            message: `Invoice ${invoice.invoice_number} from ${user?.organization_name || user?.name}. Amount: ₹${invoice.total_amount.toLocaleString()}`,
            image_data: base64Data,
            filename: `invoice_${invoice.invoice_number}_${imageFile.name}`
          };
          
          const response = await axios.post(`${API}/whatsapp/web/send-image`, messageData);
          
          if (response.data.success) {
            alert('Image sent successfully via WhatsApp!');
            setShowWhatsappImageModal(false);
            setImageFile(null);
            setImagePreview(null);
          } else {
            throw new Error(response.data.error || 'Failed to send image');
          }
        } catch (error) {
          console.error('Error sending image:', error);
          alert('Failed to send image via WhatsApp. Please try again.');
        } finally {
          setIsSendingImage(false);
        }
      };
      
      reader.readAsDataURL(imageFile);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
      setIsSendingImage(false);
    }
  };

  const openWhatsAppImageModal = () => {
    if (!invoice.client_mobile) {
      alert('Client mobile number not found');
      return;
    }
    setShowWhatsappImageModal(true);
  };

  // Send invoice PDF via WhatsApp
  const sendPDFViaWhatsApp = async () => {
    try {
      setIsSharing(true);
      
      // Check if client has mobile number
      if (!invoice.client_mobile) {
        alert('Client mobile number not available for WhatsApp sharing');
        return;
      }
      
      // For mobile devices, show options
      if (window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        const choice = confirm('Send PDF via WhatsApp?\n\nOK = Send with PDF attachment\nCancel = Send text only');
        
        if (choice) {
          // Send with PDF via WhatsApp Web
          const response = await axios.post(`${API}/invoices/${invoice.id}/send-pdf-whatsapp`, null, {
            params: { phone_number: invoice.client_mobile }
          });
          
          if (response.data.success) {
            alert('Invoice PDF sent via WhatsApp successfully!');
          } else {
            throw new Error('Failed to send PDF');
          }
        } else {
          // Fall back to text only
          sendViaWhatsApp();
          return;
        }
      } else {
        // For desktop, ask user preference
        const sendPDF = confirm('How would you like to send the invoice?\n\nOK = Send PDF attachment\nCancel = Send text summary only');
        
        if (sendPDF) {
          const response = await axios.post(`${API}/invoices/${invoice.id}/send-pdf-whatsapp`, null, {
            params: { phone_number: invoice.client_mobile }
          });
          
          if (response.data.success) {
            alert('Invoice PDF sent via WhatsApp successfully!');
          } else {
            throw new Error('Failed to send PDF');
          }
        } else {
          sendViaWhatsApp();
          return;
        }
      }
    } catch (error) {
      console.error('Error sending PDF via WhatsApp:', error);
      alert('Error sending invoice PDF via WhatsApp: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSharing(false);
    }
  };

  // Download invoice as PDF
  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Send via Telegram
  const sendViaTelegram = () => {
    const invoiceText = generateInvoiceText();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(invoiceText)}`;
    window.open(telegramUrl, '_blank');
  };

  // Send via Email
  const sendViaEmail = () => {
    const subject = `Invoice ${invoice.invoice_number} - ${invoice.client_name}`;
    const body = generateInvoiceText();
    const mailtoUrl = `mailto:${invoice.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  // Native mobile sharing
  const shareInvoice = async () => {
    const invoiceText = generateInvoiceText();
    
    if (navigator.share) {
      // Native sharing API (mobile)
      try {
        await navigator.share({
          title: `Invoice ${invoice.invoice_number}`,
          text: invoiceText,
          url: window.location.href
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(invoiceText);
        alert('Invoice details copied to clipboard!');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        alert('Unable to copy. Please use the other sharing options.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8 print:mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{user?.gstin ? 'TAX INVOICE' : 'INVOICE'}</h1>
            <p className="text-gray-600">#{invoice.invoice_number}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-600">{user?.organization_name || 'Organization'}</div>
            <div className="text-sm text-gray-600">
              {user?.address && <div>{user.address}</div>}
              {(user?.city || user?.state || user?.pincode) && (
                <div>{[user?.city, user?.state].filter(Boolean).join(', ')}{user?.pincode && ` - ${user.pincode}`}</div>
              )}
              <div>{user?.email}</div>
              {user?.mobile && <div>Mobile: {user.mobile}</div>}
              {user?.gstin && <div><strong>GSTIN:</strong> {user.gstin}</div>}
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Bill To:</h3>
            <div className="text-gray-700">
              <div className="font-medium">{invoice.client_name}</div>
              {invoice.organization_name && <div>{invoice.organization_name}</div>}
              {invoice.client?.address && <div>{invoice.client.address}</div>}
              {(invoice.client?.city || invoice.client?.state || invoice.client?.pincode) && (
                <div>{[invoice.client?.city, invoice.client?.state].filter(Boolean).join(', ')}{invoice.client?.pincode && ` - ${invoice.client.pincode}`}</div>
              )}
              {invoice.client_email && <div>Email: {invoice.client_email}</div>}
              {invoice.client_mobile && <div>Mobile: {invoice.client_mobile}</div>}
              {invoice.client_gstin && (
                <div className="mt-1">
                  <span className="font-medium">GSTIN:</span> {invoice.client_gstin}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="space-y-2">
              <div>
                <span className="font-medium text-gray-600">Invoice Date:</span>
                <div>{new Date(invoice.invoice_date).toLocaleDateString()}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Place of Supply:</span>
                <div>{user?.state || 'Not Specified'}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'overdue' ? 'destructive' : 'secondary'}>
                  {invoice.status.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="mb-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>{invoice.service_name}</TableCell>
                <TableCell>{invoice.service_description || '-'}</TableCell>
                <TableCell className="text-right">₹{invoice.rate}</TableCell>
                <TableCell className="text-right">{invoice.quantity || 1}</TableCell>
                <TableCell className="text-right">₹{invoice.amount}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Invoice Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{invoice.base_amount || invoice.amount}</span>
            </div>
            
            {(() => {
              const gstBreakdown = calculateGSTBreakdown(invoice, user);
              
              if (gstBreakdown.type === 'intrastate') {
                return (
                  <>
                    <div className="flex justify-between">
                      <span>CGST ({gstBreakdown.cgstRate}%):</span>
                      <span>₹{gstBreakdown.cgstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SGST ({gstBreakdown.sgstRate}%):</span>
                      <span>₹{gstBreakdown.sgstAmount.toFixed(2)}</span>
                    </div>
                  </>
                );
              } else if (gstBreakdown.type === 'interstate') {
                return (
                  <div className="flex justify-between">
                    <span>IGST ({gstBreakdown.igstRate}%):</span>
                    <span>₹{gstBreakdown.igstAmount.toFixed(2)}</span>
                  </div>
                );
              } else {
                return (
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>₹0.00 (No GST)</span>
                  </div>
                );
              }
            })()}
            
            <hr />
            <div className="flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span>₹{((invoice.base_amount || invoice.amount) + calculateGSTBreakdown(invoice, user).totalGst).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Notes:</h3>
            <p className="text-gray-700">{invoice.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="print:hidden space-y-4">
          {/* Primary Actions */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={onClose}>
              <Eye className="w-4 h-4 mr-2" />
              Close
            </Button>
            <div className="space-x-2">
              <Button onClick={printInvoice} variant="outline">
                <FileText className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button onClick={downloadPDF} variant="outline">
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>

          {/* Sharing Options */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg text-blue-800 flex items-center">
                <Send className="w-5 h-5 mr-2" />
                Share Invoice
              </CardTitle>
              <CardDescription className="text-blue-600">
                Send this invoice to your client via their preferred method
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mobile-First Sharing */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <Button 
                  onClick={sendPDFViaWhatsApp} 
                  disabled={isSharing}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {isSharing ? 'Sending...' : 'WhatsApp PDF'}
                </Button>
                
                <Button 
                  onClick={sendViaWhatsApp} 
                  disabled={isSharing}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50 flex items-center justify-center"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Text Only
                </Button>

                {/* Image button for WhatsApp */}
                <Button 
                  onClick={openWhatsAppImageModal}
                  variant="outline"
                  className="border-purple-600 text-purple-600 hover:bg-purple-50 flex items-center justify-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  With Image
                </Button>

                <Button 
                  onClick={sendViaTelegram}
                  className="bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Telegram
                </Button>

                <Button 
                  onClick={sendViaEmail}
                  className="bg-gray-600 hover:bg-gray-700 text-white flex items-center justify-center"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>

                <Button 
                  onClick={shareInvoice}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center"
                >
                  <Share className="w-4 h-4 mr-2" />
                  More
                </Button>
              </div>

              {/* PDF Sharing Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">📄 PDF Sharing Options:</p>
                    <ul className="text-blue-700 mt-1 space-y-1">
                      <li>• <strong>WhatsApp PDF:</strong> Sends professional PDF with logo & signature</li>
                      <li>• <strong>Text Only:</strong> Sends formatted text summary</li>
                      <li>• <strong>Download PDF:</strong> Save PDF to device for manual sharing</li>
                      <li>• <strong>Print:</strong> Opens print dialog for paper copies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Image Upload Modal */}
      {showWhatsappImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Image via WhatsApp</h3>
                <button
                  onClick={() => {
                    setShowWhatsappImageModal(false);
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>To:</strong> {invoice.client_name}</p>
                  <p><strong>Mobile:</strong> {invoice.client_mobile}</p>
                  <p><strong>Invoice:</strong> {invoice.invoice_number}</p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={imagePreview} 
                        alt="Selected" 
                        className="max-w-full max-h-48 mx-auto rounded"
                      />
                      <div className="text-sm text-gray-600">
                        {imageFile?.name} ({(imageFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                      <button
                        onClick={() => document.getElementById('whatsapp-image-input').click()}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Choose different image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-gray-600 mb-2">Select an image to send with the invoice</p>
                        <button
                          onClick={() => document.getElementById('whatsapp-image-input').click()}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Select Image
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Max size: 5MB. Supported: JPG, PNG, GIF</p>
                    </div>
                  )}
                  
                  <input
                    id="whatsapp-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowWhatsappImageModal(false);
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={isSendingImage}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendImageViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!imageFile || isSendingImage}
                  >
                    {isSendingImage ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
 

    </div>
  );
};


// Profile Component
const Profile = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    organization_name: user?.organization_name || '',
    mobile: user?.mobile || '',
    gstin: user?.gstin || '',
    state: user?.state || '',
    address: user?.address || '',
    city: user?.city || '',
    pincode: user?.pincode || '',
    profile_photo: user?.profile_photo || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  // Update formData when user data changes (for persistence)
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        organization_name: user.organization_name || '',
        mobile: user.mobile || '',
        gstin: user.gstin || '',
        state: user.state || '',
        address: user.address || '',
        city: user.city || '',
        pincode: user.pincode || '',
        profile_photo: user.profile_photo || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsPhotoUploading(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          profile_photo: event.target.result
        }));
        setIsPhotoUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      profile_photo: ''
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.put(`${API}/auth/profile`, formData);
      alert('Profile updated successfully!');
      
      // Refresh user data without full page reload
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      // Stay on profile page - no redirect
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      alert('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });


// API Key Management Component

    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.detail || error.message));
    }
  };

  const ProfileAvatar = () => {
    const initials = formData.name ? formData.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
    return (
      <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-semibold">
        {formData.profile_photo ? (
          <img 
            src={formData.profile_photo} 
            alt={formData.name} 
            className="w-24 h-24 rounded-full object-cover"
          />
        ) : (
          initials
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white/80 backdrop-blur-sm border-b border-blue-100/50 px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            User Profile
          </h1>
          <p className="text-gray-600 text-lg">Manage your account information and preferences</p>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto space-y-8">
        {/* Profile Photo Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-6 h-6 mr-2 text-blue-600" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-6">
              <ProfileAvatar />
              
              <div className="flex-1">
                <div className="flex space-x-4">
                  <input
                    type="file"
                    id="profile-photo-upload-input"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      document.getElementById('profile-photo-upload-input').click();
                    }}
                    disabled={isPhotoUploading}
                    variant="outline"
                    className="w-full max-w-xs"
                  >
                    {isPhotoUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.profile_photo ? 'Replace Photo' : 'Upload Photo'}
                      </>
                    )}
                  </Button>
                  
                  {formData.profile_photo && (
                    <Button variant="outline" onClick={removePhoto}>
                      Remove Photo
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Recommended: Square image, max 2MB. JPG, PNG supported.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Email Verification Status */}
        {!user.email_verified && (
          <Card className="bg-amber-50/80 backdrop-blur-sm border-2 border-amber-200 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-amber-800">
                <Mail className="w-6 h-6 mr-2" />
                Email Verification Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-amber-900">
                  Your email address <strong>{user.email}</strong> is not verified yet. Please verify your email to access all features.
                </p>
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/auth/resend-verification`);
                      alert('Verification email sent! Please check your inbox.');
                    } catch (error) {
                      alert('Error sending verification email: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Resend Verification Email
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* API Key Management */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="w-6 h-6 mr-2 text-purple-600" />
              API Key Management
            </CardTitle>
            <CardDescription>
              Add your OpenAI API key to enable AI-powered document summarization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <APIKeyManagement />
          </CardContent>
        </Card>


        {/* Personal Information */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-6 h-6 mr-2 text-green-600" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="organization_name">Organization Name</Label>
                  <Input
                    id="organization_name"
                    name="organization_name"
                    value={formData.organization_name}
                    onChange={handleInputChange}
                    placeholder="Your Organization/Company Name"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div>
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <Input
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                
                <div>
                  <Label htmlFor="gstin">GSTIN (Optional)</Label>
                  <Input
                    id="gstin"
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleInputChange}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Street Address, Building, Area"
                  />
                </div>
                
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City Name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="123456"
                    pattern="[0-9]{6}"
                    maxLength="6"
                  />
                </div>
              </div>

              {formData.gstin && (
                <div>
                  <Label htmlFor="state">State/Union Territory</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button type="submit" disabled={isLoading} className="bg-green-600 hover:bg-green-700">
                {isLoading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* License Information */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-6 h-6 mr-2 text-purple-600" />
              License Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>Joined Date</Label>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                  {user?.license_joined_date ? new Date(user.license_joined_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              
              <div>
                <Label>Last Renewed</Label>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                  {user?.license_last_renewed ? new Date(user.license_last_renewed).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              
              <div>
                <Label>Expiry Date</Label>
                <div className={`text-sm p-3 rounded-md ${
                  user?.license_status === 'active' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                }`}>
                  {user?.license_expiry ? new Date(user.license_expiry).toLocaleDateString() : 'N/A'}
                  <Badge className={`ml-2 ${
                    user?.license_status === 'active' ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {user?.license_status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-6 h-6 mr-2 text-red-600" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  name="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              
              <Button type="submit" variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                Change Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Settings Component  

// WhatsAppManager component moved to /app/frontend/src/pages/WhatsAppManager.jsx


// StaffManagement component moved to /app/frontend/src/pages/StaffManagement.jsx

// Tasks component moved to /app/frontend/src/pages/Tasks.jsx


// UserSettings component moved to /app/frontend/src/pages/UserSettings.jsx


// Main App Component
// App Layout Component
const AppLayout = () => {
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Don't render navigation for landing page or when no user
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  
  if (isLandingPage || !user) {
    return (
      <main key={user?.id || 'anonymous'}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/client-portal" element={<ClientPortal />} />
          <Route path="/admin" element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute requireSuperAdmin>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/platform-analytics" element={
            <ProtectedRoute requireSuperAdmin>
              <PlatformAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          } />
          <Route path="/services" element={
            <ProtectedRoute>
              <Services />
            </ProtectedRoute>
          } />
          <Route path="/client-services" element={
            <ProtectedRoute>
              <ClientServices />
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          } />
          <Route path="/receipts" element={
            <ProtectedRoute>
              <Receipts />
            </ProtectedRoute>
          } />
          <Route path="/expenses" element={
            <ProtectedRoute>
              <Expenses />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          } />
          <Route path="/content-management" element={
            <ProtectedRoute requireSuperAdmin>
              <ContentManagement />
            </ProtectedRoute>
          } />
          <Route path="/integrations" element={
            <ProtectedRoute>
              <IntegrationsManagement />
            </ProtectedRoute>
          } />
          <Route path="/reset-user" element={
            <ProtectedRoute>
              <ResetUser />
            </ProtectedRoute>
          } />
          <Route path="/backup-restore" element={
            <ProtectedRoute>
              <BackupRestore />
            </ProtectedRoute>
          } />
          <Route path="/updates" element={
            <ProtectedRoute>
              <Updates />
            </ProtectedRoute>
          } />
          <Route path="/gst-return-status" element={
            <ProtectedRoute>
              <GstReturnTracker />
            </ProtectedRoute>
          } />
          {/* GstAtGlance route temporarily disabled - component needs to be extracted */}
          {/* <Route path="/gst-at-glance" element={
            <ProtectedRoute>
              <GstAtGlance />
            </ProtectedRoute>
          } /> */}
          <Route path="/communications" element={
            <ProtectedRoute>
              <Communications />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/staff" element={
            <ProtectedRoute>
              <StaffManagement />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute requireSuperAdmin={true}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/tasks" element={
            <ProtectedRoute>
              <Tasks />
            </ProtectedRoute>
          } />
          <Route path="/task-templates" element={
            <ProtectedRoute>
              <TaskTemplates />
            </ProtectedRoute>
          } />
          <Route path="/reminder-automation" element={
            <ProtectedRoute>
              <ReminderAutomation />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AdvancedDashboard />
            </ProtectedRoute>
          } />
          <Route path="/audit-logs" element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <UserSettings />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp" element={
            <ProtectedRoute>
              <WhatsAppManager />
            </ProtectedRoute>
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/features" element={<ProjectFeatures />} />
        </Routes>
      </main>
    );
  }

  return (
    <div className="flex" key={user?.id || 'anonymous'}>
      <Navigation 
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
      />
      <main 
        className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
        } pt-16`}
      >
        <div className="p-4">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/admin" element={
              <ProtectedRoute requireSuperAdmin>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireSuperAdmin>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/platform-analytics" element={
              <ProtectedRoute requireSuperAdmin>
                <PlatformAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <Clients />
              </ProtectedRoute>
            } />
            <Route path="/bulk-upload" element={
              <ProtectedRoute>
                <BulkUpload />
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                <Services />
              </ProtectedRoute>
            } />
            {/* ClientServices route temporarily disabled - component needs to be extracted */}
            <Route path="/client-services" element={
              <ProtectedRoute>
                <ClientServices />
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            } />
            <Route path="/receipts" element={
              <ProtectedRoute>
                <Receipts />
              </ProtectedRoute>
            } />
            <Route path="/expenses" element={
              <ProtectedRoute>
                <Expenses />
              </ProtectedRoute>
            } />
            <Route path="/documents" element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            } />
            <Route path="/content-management" element={
              <ProtectedRoute requireSuperAdmin>
                <ContentManagement />
              </ProtectedRoute>
            } />
            <Route path="/gst-return-status" element={
              <ProtectedRoute>
                <GstReturnTracker />
              </ProtectedRoute>
            } />
            {/* GstAtGlance route temporarily disabled - component needs to be extracted */}
            {/* <Route path="/gst-at-glance" element={
              <ProtectedRoute>
                <GstAtGlance />
              </ProtectedRoute>
            } /> */}
            <Route path="/communications" element={
              <ProtectedRoute>
                <Communications />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/staff" element={
              <ProtectedRoute>
                <StaffManagement />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute requireSuperAdmin={true}>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute>
                <Tasks />
              </ProtectedRoute>
            } />
            <Route path="/task-templates" element={
              <ProtectedRoute>
                <TaskTemplates />
              </ProtectedRoute>
            } />
            <Route path="/reminder-automation" element={
              <ProtectedRoute>
                <ReminderAutomation />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AdvancedDashboard />
              </ProtectedRoute>
            } />
            <Route path="/audit-logs" element={
              <ProtectedRoute>
                <AuditLogs />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <UserSettings />
              </ProtectedRoute>
            } />
            <Route path="/whatsapp" element={
              <ProtectedRoute>
                <WhatsAppManager />
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                <IntegrationsManagement />
              </ProtectedRoute>
            } />
            <Route path="/reset-user" element={
              <ProtectedRoute>
                <ResetUser />
              </ProtectedRoute>
            } />
            <Route path="/backup-restore" element={
              <ProtectedRoute>
                <BackupRestore />
              </ProtectedRoute>
            } />
            <Route path="/updates" element={
              <ProtectedRoute>
                <Updates />
              </ProtectedRoute>
            } />
            <Route path="/live-variables" element={
              <ProtectedRoute requireSuperAdmin>
                <LiveVariables />
              </ProtectedRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/features" element={<ProjectFeatures />} />
            <Route path="/client-portal" element={<ClientPortal />} />
            <Route path="/portal-management" element={
              <ProtectedRoute>
                <ClientPortalManagement />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gray-50">
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
